import { extend, override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import * as ComposerImages from './ComposerImages';
import { openEmbedLink } from './EmbedLink';
import TextEditorButton from 'flarum/common/components/TextEditorButton';
import vouchaPolls from './Polls';
import vouchaCookieConsent from './CookieConsent';
import vouchaTurnstile from './Turnstile';
import vouchaPostClamp, { vouchaReplyClamp } from './PostClamp';
import vouchaPostLayout, { parkLayout, attachParkedNav, dropParkedNav, takeParkedRight, postPage, postTagSlug, FollowSlot } from './PostLayout';
import IndexPage from 'flarum/forum/components/IndexPage';
import Button from 'flarum/common/components/Button';
import Separator from 'flarum/common/components/Separator';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import Discussion from 'flarum/common/models/Discussion';
import Post from 'flarum/common/models/Post';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import { heroActions, TagAboutCard, TagHighlights, highlightIds } from './TagAbout';
import { isSportsTag, sportsTableGroup, SportsScores } from './Sports';
import './SearchTags';
import { beforeTable } from './FeedTidy';
import './Ticker';
var VISIBLE_GROUPS = 4;
var GROUPS = [{
  title: 'Location',
  slugs: ['canada', 'uk', 'usa', 'africa', 'china', 'rest-of-the-world', 'states']
}, {
  title: 'Markets & Money',
  slugs: ['finance-business', 'real-estate', 'jobs']
}, {
  title: 'Life Abroad',
  slugs: ['diaspora']
}, {
  title: 'News & Politics',
  slugs: ['politics', 'law', 'opinion']
}, {
  title: 'Sports',
  slugs: ['sports']
}, {
  title: 'Lifestyle',
  slugs: ['health', 'entertainment', 'travel', 'arts-culture', 'gaming', 'youtube']
}, {
  title: 'Tech & Learning',
  slugs: ['technology', 'education']
}, {
  title: 'Faith',
  slugs: ['christianity', 'islam']
}, {
  title: 'General',
  slugs: ['general']
}];
// Top-level tags deliberately left out of the sidebar (News has its own link in the header)
var SIDEBAR_SKIP = ['news'];
// Any other top-level tag not placed in a group above lands here, so a new tag never goes missing
var OTHER_GROUP_TITLE = 'More';

// Pinned links above the channel groups (Blind-style top section).
// route:'spa' -> internal Flarum router; route:'external' -> normal navigation;
// route:'toast' -> placeholder, shows a "coming soon" toast (My Community, pre-Phase 4).
var TOP_LINKS = [
// also in the header bar on desktop, so the sidebar keeps it for phones and tablets only (is-inHeader)
{
  title: 'My Community',
  icon: 'fas fa-users',
  href: '/community',
  route: 'spa',
  inHeader: true
}, {
  title: 'Resources',
  icon: 'fas fa-book-open',
  href: '/resources',
  route: 'spa'
}, {
  title: 'Businesses',
  icon: 'fas fa-store',
  href: '/businesses',
  route: 'spa'
}, {
  title: 'Markets',
  icon: 'fas fa-chart-line',
  href: '/markets',
  route: 'spa'
}, {
  title: 'Data Hub',
  icon: 'fas fa-database',
  href: '/data-hub',
  route: 'spa'
}];

// Footer link groups below Show More (Blind-style bottom section).
var FOOTER_GROUPS = [{
  title: 'VOUCHA',
  links: [{
    title: 'Why Voucha',
    href: '/p/32-why-voucha',
    route: 'spa'
  }, {
    title: 'Privacy',
    href: 'https://thevoucha.com/p/1-privacy-policy',
    route: 'external'
  }, {
    title: 'Cookie settings',
    href: '#cookie-settings',
    route: 'cookies'
  }]
}, {
  title: 'SUPPORT',
  links: [{
    title: 'Community Guidelines',
    href: '/d/1361-community-guidelines',
    route: 'spa'
  }, {
    title: 'Advertise',
    href: '/advertise',
    route: 'spa'
  }, {
    title: 'Updates',
    href: '/t/general',
    route: 'spa'
  }]
}];
var TRADEMARK = '\u00a9 2026 The Voucha';

// Default colour scheme: Light High Contrast when a visitor hasn't chosen one (core's default
// follows the device, which put dark-mode devices on dark pages). Members' saved choices, and a
// guest's choice for the visit, still win. Only applies while Admin > Appearance is on "Auto".
function vouchaInitColorScheme(forumDefault) {
  forumDefault = forumDefault || app.forum && app.forum.attribute('colorScheme') || 'auto';
  app.allowUserColorScheme = forumDefault === 'auto';
  var scheme;
  if (app.allowUserColorScheme) {
    var user = app.session && app.session.user;
    var pref = user && user.preferences() ? user.preferences().colorScheme : undefined;
    var guestChoice;
    try {
      guestChoice = user ? undefined : sessionStorage.getItem('colorScheme') || undefined;
    } catch (e) {}
    scheme = pref || guestChoice || 'light-hc';
  }
  app.setColorScheme(scheme || forumDefault);
  if (app.allowUserColorScheme && !app._vouchaSchemeWatch) {
    app._vouchaSchemeWatch = true;
    app.watchSystemColorSchemePreference(function () {
      vouchaInitColorScheme(forumDefault);
    });
  }
}
app.initializers.add('voucha-theme', () => {
  app.initColorScheme = vouchaInitColorScheme;

  // Blind-style polls in the feed and on the discussion page (FoF Polls)
  vouchaPolls();

  // Replies: show a readable "@name" in the composer instead of the raw mention code
  // (@"name"#p123). The code is still what gets posted: it's swapped back on submit,
  // so the reply links to the right post and notifies that person.
  override('ext:flarum/mentions/forum/mentionables/PostMention', 'replacement', function (original, post) {
    var code = original(post);
    var match = /^@"(.+)"#p\d+$/.exec(code);
    if (!match) return code;
    var readable = '@' + match[1];
    vouchaMentionCodes[readable] = code;
    return readable;
  });
  ['flarum/forum/components/ReplyComposer', 'flarum/forum/components/EditPostComposer'].forEach(function (path) {
    extend(path, 'data', function (data) {
      if (data && typeof data.content === 'string') data.content = vouchaRestoreMentions(data.content);
    });
  });

  // "Choose tags to follow" (FoF Follow Tags): only Canada, USA, UK and Finance & Business (plus any
  // already followed) until the member searches, so the list stays short on phones. Hooked by module path, so the
  // extension itself is untouched and still loads its popup on demand.
  override('ext:fof/follow-tags/forum/components/ChooseTagsToFollowModal', 'title', function () {
    return vouchaTopicFocus(this) ? 'Join communities for life abroad' : 'Join communities';
  });
  // The life-abroad focus (set when someone arrives from okjapa.com) is used once, then forgotten
  extend('ext:fof/follow-tags/forum/components/ChooseTagsToFollowModal', 'onremove', function () {
    if (vouchaTopicFocus(this)) {
      try {
        localStorage.removeItem('voucha-topic-focus');
      } catch (e) {}
    }
  });
  // Open at the top on phones: nothing focused (iOS scrolls a focused field into view), list scrolled up
  extend('ext:fof/follow-tags/forum/components/ChooseTagsToFollowModal', 'oncreate', function () {
    var el = this.element;
    var toTop = function () {
      var a = document.activeElement;
      if (a && el && el.contains(a) && a.tagName === 'INPUT' && window.innerWidth < 768) a.blur();
      [el, el && el.closest('.ModalManager'), el && el.querySelector('.Modal-content'), el && el.querySelector('.ChooseTagsToFollowModal-scroll')].forEach(function (n) {
        if (n) n.scrollTop = 0;
      });
    };
    setTimeout(toTop, 0);
    setTimeout(toTop, 350); // after the open animation
  });
  extend('ext:fof/follow-tags/forum/components/ChooseTagsToFollowModal', 'content', function (content) {
    vouchaLimitTagPicker(this, content);
  });

  // In the picker, "Follow" follows the tag straight away (no subscription-level popup);
  // clicking again unfollows. Other levels (lurking, ignoring, hiding) stay available from each
  // tag's own page, and notifications for followed tags start off (see ThemeServiceProvider).
  override('ext:fof/follow-tags/forum/components/ChooseTagsToFollowModal', 'openSubscriptionModal', function (original, tag) {
    if (!tag || tag.__vouchaSaving) return;
    var next = tag.subscription() ? null : 'follow';
    tag.__vouchaSaving = true;
    m.redraw();
    tag.save({
      subscription: next
    }).catch(function () {
      app.alerts.show({
        type: 'error'
      }, 'Couldn\u2019t update ' + tag.name() + '. Please try again.');
    }).finally(function () {
      tag.__vouchaSaving = false;
      m.redraw();
    });
  });

  // Opening a discussion from a list starts at the top (Blind-style), not at the last-read or last post.
  // Search results still jump to the matching post.
  override('flarum/forum/components/DiscussionListItem', 'getJumpTo', function (original) {
    if (this.attrs.jumpTo || this.attrs.params && this.attrs.params.q) return original();
    return 0;
  });

  // Feeds load the next page by themselves as the reader nears the bottom (no "Load more" click).
  // Uses Flarum's own paging, never loads twice at once, stops at the end; the button stays as a fallback.
  extend(DiscussionList.prototype, 'oncreate', function () {
    safe(function () {
      vouchaAutoLoad(this);
    }.bind(this));
  });
  extend(DiscussionList.prototype, 'onupdate', function () {
    safe(function () {
      vouchaAutoLoad(this);
    }.bind(this));
  });
  extend(DiscussionList.prototype, 'onremove', function () {
    if (this.__vouchaObserver) this.__vouchaObserver.disconnect();
    this.__vouchaObserver = null;
    this.__vouchaTarget = null;
  });
  extend(IndexPage.prototype, 'oncreate', function () {
    safe(applyVoucha);
  });
  extend(IndexPage.prototype, 'onupdate', function () {
    safe(applyVoucha);
  });
  extend(IndexPage.prototype, 'onremove', function () {
    // Desktop: keep the left menu and right sidebar for the next page (a post shows them too; PostLayout.js)
    if (isDesktop()) parkLayout();else removeRightSidebar();
    document.body.classList.remove('voucha-index');
    closeCardMenu();
  });

  // Desktop post pages in the same frame: header, left menu and right sidebar stay; the middle changes
  vouchaPostLayout({
    isDesktop: isDesktop,
    buildGroupedNav: buildGroupedNav,
    injectRightSidebar: injectRightSidebar,
    removeRightSidebar: removeRightSidebar,
    followLinks: vouchaFollowLinks,
    safe: safe
  });

  // Re-evaluate on viewport change so crossing 1100px flips inject <-> teardown.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      safe(applyVoucha);
    }, 150);
  });
});

// Desktop-only gate. Above 1100px we build the custom layout; below it we
// remove everything and fall back to native Flarum (which renders fine on mobile).
function isDesktop() {
  return window.innerWidth >= 1100;
}
function onIndex() {
  return !!document.querySelector('.IndexPage');
}
function applyVoucha() {
  if (!onIndex()) return;
  if (isDesktop()) {
    document.body.classList.add('voucha-index');
    injectRightSidebar();
    buildGroupedNav();
    injectCardHeaders();
    injectCardActions();
    ensureNavCollapse();
  } else {
    teardownVouchaCards();
  }
}

// ---------- Hide / show the left menu (desktop feeds, like Reddit) ----------
// A round button in the gap left of the feed folds the menu column away (body.Voucha-navCollapsed, see
// Layer 40 in forum.less); the choice is remembered in this browser. Post pages keep their menu.
var NAV_COLLAPSED_KEY = 'voucha-nav-collapsed';
function navCollapsed() {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch (e) {
    return false;
  }
}
function setNavCollapsed(on) {
  try {
    localStorage.setItem(NAV_COLLAPSED_KEY, on ? '1' : '0');
  } catch (e) {}
  document.body.classList.toggle('Voucha-navCollapsed', on);
  var btn = document.querySelector('.Voucha-navCollapse');
  if (btn) {
    btn.setAttribute('aria-expanded', on ? 'false' : 'true');
    btn.setAttribute('aria-label', on ? 'Show the menu' : 'Hide the menu');
    btn.title = on ? 'Show the menu' : 'Hide the menu';
  }
}
function ensureNavCollapse() {
  var content = document.querySelector('.IndexPage .Page-content');
  if (!content) return;
  // appended last, so Mithril's own children keep their places. The button sits in a rail as tall as the
  // feed, so it can stick near the top of the screen while the feed scrolls (Layer 40 in forum.less)
  if (!content.querySelector(':scope > .Voucha-navRail')) {
    var rail = document.createElement('div');
    rail.className = 'Voucha-navRail';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'Voucha-navCollapse';
    btn.innerHTML = '<i class="fas fa-bars" aria-hidden="true"></i>';
    btn.addEventListener('click', function () {
      setNavCollapsed(!document.body.classList.contains('Voucha-navCollapsed'));
    });
    rail.appendChild(btn);
    content.appendChild(rail);
  }
  setNavCollapsed(navCollapsed());
}

// Full teardown: remove every injected node, restore native nav, drop the
// index class so non-media voucha-index rules stop applying on mobile.
function teardownVouchaCards() {
  removeRightSidebar();
  document.querySelectorAll('.Voucha-navRail').forEach(function (el) {
    el.remove();
  });
  document.body.classList.remove('Voucha-navCollapsed');
  var gn = document.querySelector('.Voucha-groupedNav');
  if (gn) gn.remove();
  var nav = document.querySelector('.IndexPage-nav.Voucha-navReplaced');
  if (nav) nav.classList.remove('Voucha-navReplaced');
  document.querySelectorAll('.Voucha-cardHeader').forEach(function (el) {
    el.remove();
  });
  document.querySelectorAll('.Voucha-cardActions').forEach(function (el) {
    el.remove();
  });
  document.querySelectorAll('.DiscussionListItem.Voucha-cardEnhanced').forEach(function (el) {
    el.classList.remove('Voucha-cardEnhanced');
  });
  closeCardMenu(); // hides the shared body menu; element is reused, not leaked
  document.body.classList.remove('voucha-index');
}
function safe(fn) {
  try {
    fn();
  } catch (e) {
    console.warn('[voucha-theme]', e);
  }
}
function tagIsHidden(t) {
  try {
    return typeof t.isHidden === 'function' ? t.isHidden() : !!t.attribute('isHidden');
  } catch (e) {
    return false;
  }
}
function tagIcon(t) {
  try {
    return typeof t.icon === 'function' ? t.icon() : t.attribute('icon');
  } catch (e) {
    return null;
  }
}
function tagColor(t) {
  try {
    return (typeof t.color === 'function' ? t.color() : t.attribute('color')) || '#888';
  } catch (e) {
    return '#888';
  }
}
function tagParent(t) {
  try {
    return typeof t.parent === 'function' ? t.parent() : null;
  } catch (e) {
    return null;
  }
}
function tagPosition(t) {
  try {
    return (typeof t.position === 'function' ? t.position() : t.attribute('position')) || 0;
  } catch (e) {
    return 0;
  }
}

// ---------- Grouped left nav ----------

function makeNavLink(cfg, className) {
  var a = document.createElement('a');
  a.className = className;
  a.href = cfg.href;
  if (cfg.route === 'external') {
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    // normal navigation — no SPA intercept
  } else if (cfg.route === 'cookies') {
    // opened by the cookie banner's own listener for #cookie-settings links
  } else if (cfg.route === 'toast') {
    a.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      showToast(cfg.toast || 'Coming soon');
    });
  } else {
    // 'spa'
    a.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      m.route.set(a.getAttribute('href'));
    });
  }
  return a;
}

// The forum page only carries top-level tags (and the sub-tags of the tag being viewed), so a tag's sub-tags,
// and the chevron that opens them, only appeared once you'd visited it. Load every tag once, then rebuild.
var vouchaAllTags = 'no';
function loadAllTagsForNav() {
  if (vouchaAllTags !== 'no') return;
  vouchaAllTags = 'loading';
  var done = function () {
    vouchaAllTags = 'yes';
    var old = document.querySelector('.Voucha-groupedNav');
    if (old && old.parentNode) old.parentNode.removeChild(old);
    dropParkedNav();
    buildGroupedNav();
  };
  var p = app.tagList && app.tagList.load ? app.tagList.load(['children', 'parent']) : app.store.find('tags', {
    include: 'children,parent'
  });
  Promise.resolve(p).then(done).catch(function () {
    vouchaAllTags = 'yes';
  });
}
function buildGroupedNav() {
  if (window.innerWidth < 1100) return;
  var nav = document.querySelector('.IndexPage-nav .sideNav') || document.querySelector('.IndexPage-nav');
  if (!nav) return;
  loadAllTagsForNav();
  if (nav.querySelector('.Voucha-groupedNav')) return;
  // the menu kept from the last page (open groups and scroll position come with it)
  if (attachParkedNav(nav)) return;
  var tags = app.store.all('tags');
  if (!tags || !tags.length) return;
  var bySlug = {};
  var childrenOf = {};
  tags.forEach(function (t) {
    bySlug[t.slug()] = t;
    var parent = tagParent(t);
    if (parent) {
      var pid = parent.id();
      (childrenOf[pid] = childrenOf[pid] || []).push(t);
    }
  });
  var wrap = document.createElement('div');
  wrap.className = 'Voucha-groupedNav';

  // --- Top pinned links (Blind-style) ---
  if (TOP_LINKS.length) {
    var topWrap = document.createElement('div');
    topWrap.className = 'Voucha-topLinks';
    TOP_LINKS.forEach(function (cfg) {
      var link = makeNavLink(cfg, 'Voucha-topLink' + (cfg.inHeader ? ' is-inHeader' : ''));
      link.innerHTML = '<i class="Voucha-topLink-icon ' + (cfg.icon || 'fas fa-circle') + '"></i>' + '<span class="Voucha-topLink-label">' + escapeHtml(cfg.title) + '</span>';
      topWrap.appendChild(link);
    });
    wrap.appendChild(topWrap);
  }

  // Top-level tags not placed in any group (and not skipped on purpose) go in a last "More" group.
  // "Hidden" tags are included: hidden only keeps their posts out of All Discussions; the phone
  // menu lists them, so the sidebar does too.
  var placed = {};
  GROUPS.forEach(function (g) {
    g.slugs.forEach(function (s) {
      placed[s] = true;
    });
  });
  SIDEBAR_SKIP.forEach(function (s) {
    placed[s] = true;
  });
  var others = tags.filter(function (t) {
    return !tagParent(t) && !placed[t.slug()];
  }).sort(function (a, b) {
    return String(a.name()).localeCompare(String(b.name()));
  }).map(function (t) {
    return t.slug();
  });
  var groups = others.length ? GROUPS.concat([{
    title: OTHER_GROUP_TITLE,
    slugs: others
  }]) : GROUPS;
  var renderedGroups = [];
  groups.forEach(function (group) {
    var section = document.createElement('div');
    section.className = 'Voucha-navGroup';
    var heading = document.createElement('button');
    heading.className = 'Voucha-navGroup-heading';
    heading.innerHTML = '<span>' + group.title + '</span><i class="fas fa-chevron-down Voucha-navGroup-chevron"></i>';
    var body = document.createElement('div');
    body.className = 'Voucha-navGroup-body';
    heading.addEventListener('click', function () {
      var open = !section.classList.contains('is-collapsed');
      section.classList.toggle('is-collapsed', open);
    });
    var count = 0;
    group.slugs.forEach(function (slug) {
      var tag = bySlug[slug];
      if (!tag) return;
      body.appendChild(renderTag(tag, childrenOf));
      count++;
    });
    if (count === 0) return;
    section.appendChild(heading);
    section.appendChild(body);
    wrap.appendChild(section);
    renderedGroups.push(section);
  });
  if (renderedGroups.length > VISIBLE_GROUPS) {
    var hidden = renderedGroups.slice(VISIBLE_GROUPS);
    hidden.forEach(function (sec) {
      sec.classList.add('Voucha-navGroup--extra');
    });
    var moreBtn = document.createElement('button');
    moreBtn.className = 'Voucha-showMore';
    moreBtn.textContent = 'Show more channels';
    var expanded = false;
    moreBtn.addEventListener('click', function () {
      expanded = !expanded;
      hidden.forEach(function (sec) {
        sec.style.display = expanded ? '' : 'none';
      });
      moreBtn.textContent = expanded ? 'Show less channels' : 'Show more channels';
    });
    hidden.forEach(function (sec) {
      sec.style.display = 'none';
    });
    wrap.appendChild(moreBtn);
  }

  // --- Footer link groups + trademark (Blind-style bottom) ---
  if (FOOTER_GROUPS.length) {
    var footWrap = document.createElement('div');
    footWrap.className = 'Voucha-navFooter';
    FOOTER_GROUPS.forEach(function (group) {
      var sec = document.createElement('div');
      sec.className = 'Voucha-navFooter-group';
      var h = document.createElement('div');
      h.className = 'Voucha-navFooter-heading';
      h.textContent = group.title;
      sec.appendChild(h);
      group.links.forEach(function (cfg) {
        var link = makeNavLink(cfg, 'Voucha-navFooter-link');
        link.textContent = cfg.title;
        sec.appendChild(link);
      });
      footWrap.appendChild(sec);
    });
    if (TRADEMARK) {
      var tm = document.createElement('div');
      tm.className = 'Voucha-navTrademark';
      tm.textContent = TRADEMARK;
      footWrap.appendChild(tm);
    }
    wrap.appendChild(footWrap);
  }
  nav.classList.add('Voucha-navReplaced');
  nav.appendChild(wrap);
}
function renderTag(tag, childrenOf) {
  var kids = childrenOf[tag.id()] || [];
  var color = tagColor(tag);
  var icon = tagIcon(tag);
  var item = document.createElement('div');
  item.className = 'Voucha-navItem';
  var row = document.createElement('div');
  row.className = 'Voucha-navItem-row';
  var link = document.createElement('a');
  link.className = 'Voucha-navLink';
  link.href = app.route('tag', {
    tags: tag.slug()
  });
  link.style.setProperty('--tag-color', color);
  var iconHtml = icon ? '<i class="Voucha-navIcon ' + icon + '"></i>' : '<span class="Voucha-navDot" style="background:' + color + '"></span>';
  link.innerHTML = iconHtml + '<span class="Voucha-navLabel">' + escapeHtml(tag.name()) + '</span>';
  link.addEventListener('click', function (e) {
    e.preventDefault();
    m.route.set(link.getAttribute('href'));
  });
  row.appendChild(link);
  row.addEventListener("click", function (e) {
    if (e.target === row) {
      link.click();
    }
  });
  if (kids.length) {
    var toggle = document.createElement('button');
    toggle.className = 'Voucha-navToggle';
    toggle.innerHTML = '<i class="fas fa-chevron-down"></i>';
    var childWrap = document.createElement('div');
    childWrap.className = 'Voucha-navChildren';
    childWrap.style.display = 'none';
    kids.sort(function (a, b) {
      return tagPosition(a) - tagPosition(b);
    });
    kids.forEach(function (child) {
      if (tagIsHidden(child)) return;
      var c = document.createElement('a');
      c.className = 'Voucha-navChild';
      c.href = app.route('tag', {
        tags: child.slug()
      });
      c.textContent = child.name();
      c.addEventListener('click', function (e) {
        e.preventDefault();
        m.route.set(c.getAttribute('href'));
      });
      childWrap.appendChild(c);
    });
    toggle.addEventListener('click', function () {
      var open = childWrap.style.display !== 'none';
      childWrap.style.display = open ? 'none' : 'block';
      item.classList.toggle('is-open', !open);
    });
    row.appendChild(toggle);
    item.appendChild(row);
    item.appendChild(childWrap);
  } else {
    item.appendChild(row);
  }
  return item;
}

// ---------- Right sidebar ----------

// The tag being viewed ('' on the home page)
function currentTagSlug() {
  try {
    return m.route.param('tags') || '';
  } catch (e) {
    return '';
  }
}
function currentRoute() {
  try {
    return m.route.get() || '';
  } catch (e) {
    return '';
  }
}
function removeRightSidebar() {
  var rs = document.querySelector('.Voucha-rightSidebar');
  if (!rs) return;
  rs.querySelectorAll('.Voucha-sidebarAd').forEach(function (ad) {
    try {
      m.render(ad, null);
    } catch (e) {}
  });
  var aboutSlot = rs.querySelector('.Voucha-tagAboutSlot');
  if (aboutSlot) {
    try {
      m.mount(aboutSlot, null);
    } catch (e) {}
  }
  var followSlot = rs.querySelector('.Voucha-followSlot');
  if (followSlot) {
    try {
      m.mount(followSlot, null);
    } catch (e) {}
  }
  rs.remove();
}

// Most Read follows the page: on the governors and kingdoms pages, posts about states, politics and
// elections (with their sub-tags); elsewhere, the tag being viewed (or everything on the home page)
function mostReadSlug() {
  if (/^\/community\/(governors|kingdoms)\b/.test(window.location.pathname)) return 'states,politics,elections';
  return sidebarTagSlug();
}

// The tag the right sidebar is about: the tag page's, or on a post page the post's main tag
function sidebarTagSlug() {
  return postPage() ? postTagSlug() : currentTagSlug();
}
function injectRightSidebar() {
  if (!isDesktop()) return;
  var container = document.querySelector('.IndexPage .Page-container') || document.querySelector('.DiscussionPage .Page-container');
  if (!container) return;
  var slug = mostReadSlug();
  var route = currentRoute();
  // the sidebar kept from the last page (home, a tag or a post) moves into this one, then catches up below
  var existing = container.querySelector('.Voucha-rightSidebar') || takeParkedRight(container);
  if (existing) {
    // Flarum keeps IndexPage mounted across tags, and a post shows its own tag: a new tag refreshes Most Read...
    if (existing.getAttribute('data-tag') !== slug) {
      existing.setAttribute('data-tag', slug);
      setMostReadHeading(slug);
      loadMostRead(slug);
      loadMasterplans(sidebarTagSlug());
      loadTopStocks(sidebarTagSlug());
    }
    // ...and every new page view gets a fresh ad pick, so the sidebar rotates
    if (existing.getAttribute('data-route') !== route) {
      existing.setAttribute('data-route', route);
      mountSidebarAd(sidebarTagSlug());
    }
    return;
  }
  var aside = document.createElement('aside');
  aside.className = 'Voucha-rightSidebar';
  aside.setAttribute('data-tag', slug);
  aside.setAttribute('data-route', route);
  aside.innerHTML = '<div class="Voucha-tagAboutSlot"></div>' + '<div class="Voucha-followSlot"></div>' + '<div class="Voucha-sidebarAd"></div>' + '<div class="Voucha-rightSidebar-section">' + '<div class="Voucha-rightSidebar-heading">Most Read</div>' + '<div class="Voucha-mostRead"><div class="Voucha-mostRead-loading">Loading...</div></div>' + '</div>' + '<div class="Voucha-sidebarAd Voucha-sidebarAd--second"></div>';
  container.appendChild(aside);
  // About card for the tag being viewed (reads the slug on every redraw, so it follows tag changes)
  var aboutSlot = aside.querySelector('.Voucha-tagAboutSlot');
  if (aboutSlot) m.mount(aboutSlot, {
    view: function () {
      var s = sidebarTagSlug();
      return s ? m(TagAboutCard, {
        slug: s,
        variant: 'side',
        extra: function () {
          return sportsTableGroup(s);
        }
      }) : null;
    }
  });
  // post pages: "Follow us" on Google / WhatsApp (they sat in the post's own column before)
  var followSlot = aside.querySelector('.Voucha-followSlot');
  if (followSlot) m.mount(followSlot, FollowSlot);
  setMostReadHeading(slug);
  loadMostRead(slug);
  loadMasterplans(sidebarTagSlug());
  loadTopStocks(sidebarTagSlug());
  mountSidebarAd(sidebarTagSlug());
}

/**
 * Finance & Business only: the NGX's five largest companies by market value (symbol, price, 7-day
 * change), above Most Read, linking to the markets page. Hidden on every other page.
 */
var vouchaStocks = null;
function isFinanceTag(slug) {
  if (!slug) return false;
  var tag = (app.store.all('tags') || []).find(function (t) {
    return t.slug() === slug;
  });
  return slug === 'finance-business' || tag && /^finance\s*(&|and)\s*business$/i.test(tag.name());
}
function renderTopStocks(box) {
  var esc = function (t) {
    var d = document.createElement('div');
    d.textContent = String(t == null ? '' : t);
    return d.innerHTML;
  };
  var top = (vouchaStocks || []).filter(function (s) {
    return Number(s.current_price) > 0;
  }).sort(function (a, b) {
    return (b.market_cap || 0) - (a.market_cap || 0);
  }).slice(0, 5);
  if (!top.length) {
    box.innerHTML = '';
    return;
  }
  var rows = top.map(function (s) {
    var c = Number(s.pct_change_7d);
    var tone = !isFinite(c) || c === 0 ? '' : c > 0 ? ' is-up' : ' is-down';
    var change = !isFinite(c) ? '–' : (c > 0 ? '+' : '') + c.toFixed(2) + '%';
    var price = '₦' + Number(s.current_price).toLocaleString('en-US', {
      maximumFractionDigits: 2
    });
    return '<a class="Voucha-topStocks-row" href="/markets/' + esc(String(s.symbol).toLowerCase()) + '" title="' + esc(s.name) + '"><span class="Voucha-topStocks-sym">' + esc(s.symbol) + '</span><span class="Voucha-topStocks-price">' + esc(price) + '</span><span class="Voucha-topStocks-chg' + tone + '">' + esc(change) + '</span></a>';
  }).join('');
  box.innerHTML = '<div class="Voucha-rightSidebar-heading">Top companies on the NGX</div>' + '<div class="Voucha-topStocks-head"><span>Symbol</span><span>Price</span><span>7D</span></div>' + rows + '<a class="Voucha-topStocks-more" href="/markets/ngx">All NGX stocks →</a>';
  box.querySelectorAll('a').forEach(function (a) {
    a.addEventListener('click', function (e) {
      if (!e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        m.route.set(a.getAttribute('href'));
      }
    });
  });
}
// (The About card's "Top companies on the NGX" section is gone: the ticker under the header shows them, Ticker.js)

function loadTopStocks(slug) {
  var box = document.querySelector('.Voucha-topStocks');
  if (!box) return;
  if (!isFinanceTag(slug)) {
    box.innerHTML = '';
    return;
  }
  if (vouchaStocks) {
    renderTopStocks(box);
    return;
  }
  app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/voucha-markets/data',
    errorHandler: function () {}
  }).then(function (r) {
    var entry = r && r.markets && r.markets.stocks;
    vouchaStocks = entry && entry.data && entry.data.stocks || [];
    var current = document.querySelector('.Voucha-topStocks');
    if (current && isFinanceTag(document.querySelector('.Voucha-rightSidebar') && document.querySelector('.Voucha-rightSidebar').getAttribute('data-tag'))) renderTopStocks(current);
  }).catch(function () {});
}

// State masterplans: on a state's tag page, a box above Most Read listing its published plans (hidden if none)
var masterplansRequest = 0;
function loadMasterplans(slug) {
  var sidebar = document.querySelector('.Voucha-rightSidebar');
  if (!sidebar) return;
  var box = sidebar.querySelector('.Voucha-masterplans');
  if (box) box.remove();
  if (!slug || slug.indexOf(',') !== -1) return;
  var mine = ++masterplansRequest;
  app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/datahub/masterplans?tag=' + encodeURIComponent(slug),
    errorHandler: function () {}
  }).then(function (r) {
    var plans = r && r.plans || [];
    if (mine !== masterplansRequest || !plans.length) return;
    var el = document.createElement('div');
    el.className = 'Voucha-masterplans';
    var esc = function (t) {
      var d = document.createElement('div');
      d.textContent = String(t || '');
      return d.innerHTML;
    };
    el.innerHTML = '<h4 class="Voucha-rightSidebar-heading"><i class="fas fa-drafting-compass" aria-hidden="true"></i> Masterplans</h4><ul>' + plans.map(function (p) {
      return '<li><a href="' + esc(p.url) + '" target="_blank" rel="noopener">' + esc(p.title) + '</a>' + (p.year ? '<span>' + esc(p.year) + '</span>' : '') + '</li>';
    }).join('') + '</ul>';
    var mostRead = sidebar.querySelector('.Voucha-rightSidebar-heading');
    sidebar.insertBefore(el, mostRead || sidebar.firstChild);
  }).catch(function () {});
}
function setMostReadHeading(slug) {
  var heading = document.querySelector('.Voucha-rightSidebar-heading');
  if (!heading) return;
  var tag = slug ? (app.store.all('tags') || []).find(function (t) {
    return t.slug() === slug;
  }) : null;
  heading.textContent = tag ? 'Most Read in ' + tag.name() : 'Most Read';
}

// Sidebar ad (voucha/ads). Draws nothing when no booking targets this tag.
// The sidebar is built outside Mithril, so the ad is drawn into it with voucha/ads' fillSlot().
// Two slots: one above Most Read, one below it. Each can be switched off in the theme's settings.
function mountSidebarAd(slug, tries) {
  var first = document.querySelector('.Voucha-sidebarAd:not(.Voucha-sidebarAd--second)');
  var second = document.querySelector('.Voucha-sidebarAd--second');
  if (!first && !second) return;
  if (!app.vouchaAds || !app.vouchaAds.fillSlot) {
    tries = tries || 0;
    if (tries < 20) setTimeout(function () {
      mountSidebarAd(slug, tries + 1);
    }, 250);
    return;
  }
  var on1 = app.forum.attribute('vouchaAdsSidebar') !== false;
  var on2 = app.forum.attribute('vouchaAdsSidebarSecond') !== false;
  var opts = {
    placement: 'banner',
    tag: slug || '',
    empty: vouchaAdPlaceholder
  };
  var clear = function (el) {
    try {
      m.render(el, null);
    } catch (e) {}
    el.innerHTML = '';
  };
  var useSecond = second && on2;
  if (second && !on2) clear(second);
  if (useSecond) clear(second); // nothing in the lower slot until we know what the top one shows

  // The lower slot is filled once the top one has its ad, and never with that same ad: with a single
  // booking it shows the "Ad space available" card straight away (no repeat, then swap)
  var route = window.location.pathname;
  var fillSecond = function (ad) {
    if (!useSecond || !second.isConnected || window.location.pathname !== route) return;
    app.vouchaAds.fillSlot(second, Object.assign({}, opts, {
      avoid: ad && ad.id ? [ad.id] : null
    }));
  };
  if (first && on1) {
    app.vouchaAds.fillSlot(first, Object.assign({}, opts, {
      onAd: fillSecond
    }));
  } else {
    if (first) clear(first);
    fillSecond(null);
  }
}
var mostReadRequest = 0;
function loadMostRead(slug) {
  if (!isDesktop()) return;
  var box = document.querySelector('.Voucha-mostRead');
  if (box) box.innerHTML = '<div class="Voucha-mostRead-loading">Loading...</div>';
  var mine = ++mostReadRequest;

  // Ranked on the server: past year, this tag and its sub-tags, views + replies + likes
  app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/voucha-theme/most-read?tag=' + encodeURIComponent(slug || '')
  }).then(function (r) {
    if (mine !== mostReadRequest) return; // a newer tag was requested meanwhile
    renderMostRead(r && r.items || []);
  }).catch(function (e) {
    console.warn('[voucha-theme] mostRead', e);
    var b = document.querySelector('.Voucha-mostRead');
    if (b) b.innerHTML = '<div class="Voucha-mostRead-empty">No data</div>';
  });
}
function viewCountOf(d) {
  var v = d.attribute('viewCount');
  if (v == null) v = d.attribute('view_count');
  return v == null ? 0 : Number(v) || 0;
}
function renderMostRead(items) {
  var box = document.querySelector('.Voucha-mostRead');
  if (!box) return;
  if (!items || !items.length) {
    box.innerHTML = '<div class="Voucha-mostRead-empty">Nothing here yet</div>';
    return;
  }
  var html = '';
  items.forEach(function (d) {
    var href = app.route('discussion', {
      id: d.id + (d.slug ? '-' + d.slug : '')
    });
    var timeText = '';
    try {
      if (d.createdAt) timeText = relativeTime(new Date(d.createdAt));
    } catch (e) {}
    html += '<div class="Voucha-mostRead-item">';
    html += '<div class="Voucha-mostRead-top">';
    if (d.tag) html += '<span class="Voucha-mostRead-tag" style="--tag-color:' + escapeHtml(d.tag.color || '#888') + '">' + escapeHtml(d.tag.name) + '</span>';
    if (timeText) html += '<span class="Voucha-mostRead-time">' + timeText + '</span>';
    html += '<span class="Voucha-mostRead-views"><i class="fas fa-eye"></i> ' + formatCount(d.views || 0) + '</span>';
    html += '</div>';
    html += '<a class="Voucha-mostRead-title" href="' + href + '">' + escapeHtml(d.title) + '</a>';
    html += '</div>';
  });
  box.innerHTML = html;
  box.querySelectorAll('.Voucha-mostRead-title').forEach(function (a) {
    a.addEventListener('click', function (e) {
      e.preventDefault();
      m.route.set(a.getAttribute('href'));
    });
  });
}
function tagColor2(t) {
  try {
    return (typeof t.color === 'function' ? t.color() : t.attribute('color')) || '#888';
  } catch (e) {
    return '#888';
  }
}

// ---------- Card action bar (share + more) ----------

function injectCardActions() {
  if (window.innerWidth < 1100) return; // desktop-only; matches card CSS scope

  var statsList = document.querySelectorAll('.DiscussionListItem-stats');
  if (!statsList.length) return;
  statsList.forEach(function (statsEl) {
    if (statsEl.querySelector('.Voucha-cardActions')) return; // already added

    var item = statsEl.closest('.DiscussionListItem');
    if (!item) return;
    var mainLink = item.querySelector('.DiscussionListItem-main');
    var titleEl = item.querySelector('.DiscussionListItem-title');
    var href = mainLink ? mainLink.getAttribute('href') : null;
    if (!href) return;
    var url = absoluteUrl(href);
    var title = titleEl ? titleEl.textContent.trim() : document.title;
    var actions = document.createElement('div');
    actions.className = 'Voucha-cardActions';
    var shareBtn = document.createElement('button');
    shareBtn.type = 'button';
    shareBtn.className = 'Voucha-cardAction Voucha-cardAction--share';
    shareBtn.setAttribute('aria-label', 'Share');
    shareBtn.innerHTML = '<i class="fas fa-arrow-up-from-bracket"></i>';
    shareBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      shareLink(url, title);
    });
    var moreBtn = document.createElement('button');
    moreBtn.type = 'button';
    moreBtn.className = 'Voucha-cardAction Voucha-cardAction--more';
    moreBtn.setAttribute('aria-label', 'More');
    moreBtn.innerHTML = '<i class="fas fa-ellipsis"></i>';
    moreBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      toggleCardMenu(moreBtn, url, title);
    });

    // Save (voucha-feed's Saved posts), just before Share
    var idMatch = /\/d\/(\d+)/.exec(href);
    var disc = idMatch && app.vouchaSaved ? app.store.getById('discussions', idMatch[1]) : null;
    if (disc) {
      var saveBtn = document.createElement('button');
      saveBtn.type = 'button';
      saveBtn.className = 'Voucha-cardAction Voucha-cardAction--save';
      var paintSave = function () {
        var on = app.vouchaSaved.isSaved(disc);
        saveBtn.classList.toggle('is-saved', on);
        saveBtn.setAttribute('aria-label', on ? 'Saved' : 'Save');
        saveBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        saveBtn.title = on ? 'Saved' : 'Save';
        saveBtn.innerHTML = '<i class="' + (on ? 'fas' : 'far') + ' fa-bookmark"></i>';
      };
      paintSave();
      saveBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        app.vouchaSaved.toggle(disc).then(paintSave);
        paintSave();
      });
      actions.appendChild(saveBtn);
    }
    actions.appendChild(shareBtn);
    actions.appendChild(moreBtn);
    statsEl.appendChild(actions);
  });
}
function absoluteUrl(href) {
  try {
    return new URL(href, window.location.origin).href;
  } catch (e) {
    return window.location.origin + href;
  }
}

// ---------- Share ----------

function shareLink(url, title) {
  if (navigator.share) {
    navigator.share({
      title: title,
      url: url
    }).catch(function () {});
    return;
  }
  copyLink(url); // desktop fallback
}

// ---------- Copy link + toast ----------

function copyLink(url) {
  var done = function () {
    showToast('Link copied');
  };
  var fail = function () {
    showToast('Copy failed');
  };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url).then(done, function () {
      if (legacyCopy(url)) done();else fail();
    });
  } else if (legacyCopy(url)) {
    done();
  } else {
    fail();
  }
}
function legacyCopy(text) {
  try {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    var ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}
var toastTimer = null;
function showToast(msg) {
  var t = document.querySelector('.Voucha-toast');
  if (!t) {
    t = document.createElement('div');
    t.className = 'Voucha-toast';
    document.body.appendChild(t);
  }
  t.textContent = msg;
  t.classList.remove('is-visible');
  void t.offsetWidth; // restart transition
  t.classList.add('is-visible');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(function () {
    t.classList.remove('is-visible');
  }, 2000);
}

// ---------- Reusable card menu (Copy Link; extensible) ----------

var cardMenu = null;
var cardMenuOpen = false;
function getCardMenu() {
  if (cardMenu) return cardMenu;
  cardMenu = document.createElement('div');
  cardMenu.className = 'Voucha-cardMenu';
  cardMenu.style.display = 'none';
  document.body.appendChild(cardMenu);
  document.addEventListener('click', function (e) {
    if (!cardMenuOpen) return;
    if (cardMenu.contains(e.target)) return;
    closeCardMenu();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeCardMenu();
  });
  window.addEventListener('scroll', function () {
    if (cardMenuOpen) closeCardMenu();
  }, true);
  window.addEventListener('resize', function () {
    if (cardMenuOpen) closeCardMenu();
  });
  return cardMenu;
}
function toggleCardMenu(button, url, title) {
  var menu = getCardMenu();
  if (cardMenuOpen && menu.__trigger === button) {
    closeCardMenu();
    return;
  }
  menu.innerHTML = '';
  menu.appendChild(menuItem('fas fa-link', 'Copy Link', function () {
    copyLink(url);
    closeCardMenu();
  }));
  menu.__trigger = button;
  menu.style.display = 'block';
  var r = button.getBoundingClientRect();
  var mw = menu.offsetWidth;
  var top = r.bottom + window.scrollY + 6;
  var left = r.right + window.scrollX - mw;
  if (left < 8) left = 8;
  menu.style.top = top + 'px';
  menu.style.left = left + 'px';
  cardMenuOpen = true;
}
function menuItem(iconClass, label, onClick) {
  var b = document.createElement('button');
  b.type = 'button';
  b.className = 'Voucha-cardMenu-item';
  b.innerHTML = '<i class="' + iconClass + '"></i><span>' + escapeHtml(label) + '</span>';
  b.addEventListener('click', function (e) {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  });
  return b;
}
function closeCardMenu() {
  if (!cardMenu) return;
  cardMenu.style.display = 'none';
  cardMenu.__trigger = null;
  cardMenuOpen = false;
}

// ---------- Blind-style card header (native tags + time + author) ----------

function injectCardHeaders() {
  if (window.innerWidth < 1100) return; // desktop-only; matches card CSS scope

  var items = document.querySelectorAll('.DiscussionListItem');
  if (!items.length) return;
  items.forEach(function (item) {
    var author = item.querySelector('.DiscussionListItem-author');
    if (!author) return;
    if (author.querySelector('.Voucha-cardHeader')) return; // already added

    // time from terminalPost <time datetime>, rendered Blind-short
    var timeText = '';
    var timeEl = item.querySelector('.DiscussionListItem-info .item-terminalPost time[datetime]');
    if (timeEl) {
      var dt = new Date(timeEl.getAttribute('datetime'));
      if (!isNaN(dt.getTime())) timeText = relativeTime(dt);
    }

    // author: OP from avatar href, fallback to last replier
    var name = '';
    var avatarLink = author.querySelector('.DiscussionListItem-author-avatar[href]');
    if (avatarLink) {
      var mm = avatarLink.getAttribute('href').match(/\/u\/([^\/?#]+)/);
      if (mm) {
        try {
          name = decodeURIComponent(mm[1]);
        } catch (e) {
          name = mm[1];
        }
      }
    }
    if (!name) {
      var repl = item.querySelector('.item-terminalPost .username');
      if (repl) name = repl.textContent.trim();
    }
    var header = document.createElement('div');
    header.className = 'Voucha-cardHeader';
    var line1 = document.createElement('div');
    line1.className = 'Voucha-cardHeader-line1';

    // Clone native tags (keeps icons/colours); wire each pill to its tag route
    var tagsSrc = item.querySelector('.DiscussionListItem-info .item-tags .TagsLabel');
    if (tagsSrc) {
      var tagsClone = tagsSrc.cloneNode(true);
      tagsClone.classList.add('Voucha-cardHeader-tags');
      tagsClone.querySelectorAll('.TagLabel').forEach(function (labelEl) {
        var nameEl = labelEl.querySelector('.TagLabel-name');
        var nm = nameEl ? nameEl.textContent.trim() : '';
        var slug = slugForTagName(nm);
        if (slug) {
          labelEl.style.cursor = 'pointer';
          labelEl.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            m.route.set(app.route('tag', {
              tags: slug
            }));
          });
        }
      });
      line1.appendChild(tagsClone);
    }
    if (timeText) {
      if (tagsSrc) {
        var sep = document.createElement('span');
        sep.className = 'Voucha-cardHeader-sep';
        sep.textContent = '\u00b7';
        line1.appendChild(sep);
      }
      var tm = document.createElement('span');
      tm.className = 'Voucha-cardHeader-time';
      tm.textContent = timeText;
      line1.appendChild(tm);
    }
    header.appendChild(line1);
    if (name) {
      var line2 = document.createElement('div');
      line2.className = 'Voucha-cardHeader-line2';

      // Prefer the exact OP profile URL from the avatar link; else best-effort /u/<name>
      var userHref = avatarLink ? avatarLink.getAttribute('href') : null;
      if (!userHref && name) userHref = '/u/' + encodeURIComponent(name);
      if (userHref) {
        var ulink = document.createElement('a');
        ulink.className = 'Voucha-cardHeader-user';
        ulink.href = userHref;
        ulink.textContent = name;
        ulink.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          m.route.set(userHref);
        });
        line2.appendChild(ulink);
      } else {
        line2.textContent = name;
      }
      header.appendChild(line2);
    }
    author.appendChild(header);
    item.classList.add('Voucha-cardEnhanced');
  });
}
function slugForTagName(name) {
  try {
    var tags = app.store.all('tags');
    for (var i = 0; i < tags.length; i++) {
      if (tags[i].name() === name) return tags[i].slug();
    }
  } catch (e) {}
  return null;
}

// ---------- Utils ----------

function relativeTime(date) {
  var now = Date.now();
  var diff = Math.floor((now - date.getTime()) / 1000);
  if (diff < 60) return 'now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h';
  if (diff < 604800) return Math.floor(diff / 86400) + 'd';
  if (diff < 2592000) return Math.floor(diff / 604800) + 'w';
  // up to 11 months, then years ("1y" from 12 months)
  var days = Math.floor(diff / 86400);
  if (Math.floor(days / 30) < 12) return Math.floor(days / 30) + 'mo';
  return Math.max(1, Math.floor(days / 365)) + 'y';
}
function formatCount(n) {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}
function escapeHtml(s) {
  var div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

// ---------- Tag picker: a few featured tags; the rest through search ----------
// Shown in "Choose tags to follow" before searching (slugs, in this order)
var VOUCHA_PICKER_FEATURED = ['canada', 'usa', 'uk', 'finance-business'];

// Life-abroad focus, for people who arrive from okjapa.com (Japa Compass): only Diaspora and its topics, and
// places abroad (the Location group without Nigeria's States) with their sub-tags.
var VOUCHA_FOCUS_DIASPORA = ['diaspora', 'canada', 'usa', 'uk', 'africa', 'china', 'rest-of-the-world'];

/** True when the picker was opened for an okjapa arrival (by the results page, or within 3 days of arriving) */
function vouchaTopicFocus(modal) {
  if (modal && modal.attrs && modal.attrs.vouchaFocus === 'diaspora') return true;
  try {
    var f = JSON.parse(localStorage.getItem('voucha-topic-focus') || 'null');
    return !!(f && f.focus === 'diaspora' && Date.now() - f.at < 3 * 86400000);
  } catch (e) {
    return false;
  }
}
function vouchaTagParentSlug(tag) {
  var p = tag && tag.parent ? tag.parent() : null;
  return p && p.slug ? p.slug() : '';
}
function vouchaLimitTagPicker(modal, content) {
  if (!Array.isArray(content)) return;
  var tbody = vnodeFind(content[0], function (v) {
    return v.tag === 'tbody';
  });
  if (!tbody || !Array.isArray(tbody.children)) return;

  // Mithril wraps the mapped rows in a fragment ('['): work with whichever array holds the <tr>s
  var holder = tbody.children.length === 1 && tbody.children[0] && tbody.children[0].tag === '[' ? tbody.children[0] : tbody;
  var rows = (holder.children || []).filter(function (r) {
    return r && r.tag === 'tr';
  });

  // Our own class names on the list and each card, so the styling never depends on table selectors
  // Always give each element its OWN attrs object: Mithril shares one empty attrs object between
  // elements created without attributes, so editing it in place would label every element alike.
  tbody.attrs = Object.assign({}, tbody.attrs, {
    className: 'VouchaTagPicker-list'
  });
  rows.forEach(function (tr) {
    var button = vnodeFind(tr, function (v) {
      return v.attrs && Object.prototype.hasOwnProperty.call(v.attrs, 'subscription');
    });
    var sub = button ? button.attrs.subscription : null;
    tr.attrs = Object.assign({}, tr.attrs, {
      className: 'VouchaTagPicker-card' + (sub === 'follow' || sub === 'lurk' ? ' is-followed' : '')
    });
  });

  // Search box above the cards (always present, so it keeps focus while typing)
  var query = (modal.vouchaQuery || '').trim().toLowerCase();
  var scroll = content[0];
  if (scroll && Array.isArray(scroll.children)) {
    scroll.children = [m('div', {
      className: 'VouchaTagPicker-search'
    }, [m('i', {
      className: 'fas fa-search',
      'aria-hidden': 'true'
    }), m('input', {
      className: 'FormControl',
      type: 'search',
      placeholder: vouchaTopicFocus(modal) ? 'Search places and communities' : 'Search communities',
      value: modal.vouchaQuery || '',
      oninput: function (e) {
        modal.vouchaQuery = e.target.value;
      }
    })])].concat(scroll.children);
  }
  if (vouchaTopicFocus(modal)) {
    vouchaFocusRows(holder, rows, query, modal);
    return;
  }
  if (query) {
    var matches = rows.filter(function (tr) {
      var cells = Array.isArray(tr.children) ? tr.children : [];
      return (vnodeText(cells[0]) + ' ' + vnodeText(cells[1])).toLowerCase().indexOf(query) !== -1;
    });
    holder.children = matches.length ? matches : [m('tr', {
      className: 'VouchaTagPicker-card VouchaTagPicker-more'
    }, m('td', 'No communities match "' + modal.vouchaQuery.trim() + '"'))];
    return;
  }

  // No search: only the featured tags (plus any already followed); everything else is a search away
  var byName = {};
  (app.store.all('tags') || []).forEach(function (t) {
    byName[t.name()] = t;
  });
  var info = rows.map(function (tr, i) {
    var nameCell = Array.isArray(tr.children) ? tr.children[0] : null;
    var tag = byName[vnodeText(nameCell).trim()];
    var button = vnodeFind(tr, function (v) {
      return v.attrs && Object.prototype.hasOwnProperty.call(v.attrs, 'subscription');
    });
    var sub = button ? button.attrs.subscription : null;
    var slug = tag && tag.slug ? tag.slug() : '';
    return {
      tr: tr,
      i: i,
      rank: VOUCHA_PICKER_FEATURED.indexOf(slug),
      followed: sub === 'follow' || sub === 'lurk'
    };
  });
  var shown = info.filter(function (x) {
    return x.rank !== -1 || x.followed;
  }).sort(function (a, b) {
    return (a.rank === -1 ? 99 : a.rank) - (b.rank === -1 ? 99 : b.rank) || a.i - b.i;
  });
  var out = shown.map(function (x) {
    return x.tr;
  });
  if (shown.length < rows.length) {
    out.push(m('tr', {
      className: 'VouchaTagPicker-card VouchaTagPicker-more'
    }, m('td', {
      className: 'VouchaTagPicker-hint'
    }, 'Search above to find more of our ' + rows.length + ' communities')));
  }
  holder.children = out;
}

/** Life-abroad focus: Diaspora first, then its topics, then places abroad and their sub-tags */
function vouchaFocusRows(holder, rows, query, modal) {
  var byName = {};
  (app.store.all('tags') || []).forEach(function (t) {
    byName[t.name()] = t;
  });
  var info = rows.map(function (tr, i) {
    var cells = Array.isArray(tr.children) ? tr.children : [];
    var tag = byName[vnodeText(cells[0]).trim()];
    var slug = tag && tag.slug ? tag.slug() : '';
    var parent = vouchaTagParentSlug(tag);
    var rank = slug === 'diaspora' ? 0 : parent === 'diaspora' ? 1 : VOUCHA_FOCUS_DIASPORA.indexOf(slug) !== -1 ? 2 + VOUCHA_FOCUS_DIASPORA.indexOf(slug) / 100 : VOUCHA_FOCUS_DIASPORA.indexOf(parent) !== -1 ? 3 : -1;
    return {
      tr: tr,
      i: i,
      rank: rank,
      text: (vnodeText(cells[0]) + ' ' + vnodeText(cells[1])).toLowerCase()
    };
  }).filter(function (x) {
    return x.rank !== -1;
  });
  var shown = query ? info.filter(function (x) {
    return x.text.indexOf(query) !== -1;
  }) : info;
  shown.sort(function (a, b) {
    return a.rank - b.rank || a.i - b.i;
  });
  holder.children = shown.length ? shown.map(function (x) {
    return x.tr;
  }) : [m('tr', {
    className: 'VouchaTagPicker-card VouchaTagPicker-more'
  }, m('td', 'No places or life-abroad communities match "' + (modal.vouchaQuery || '').trim() + '"'))];
}
function vnodeFind(node, test) {
  if (!node || typeof node !== 'object') return null;
  if (Array.isArray(node)) {
    for (var i = 0; i < node.length; i++) {
      var hit = vnodeFind(node[i], test);
      if (hit) return hit;
    }
    return null;
  }
  if (test(node)) return node;
  return vnodeFind(node.children, test);
}
function vnodeText(node) {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(vnodeText).join('');
  if (node.tag === '#') return String(node.children == null ? '' : node.children);
  if (typeof node.text === 'string' || typeof node.text === 'number') return String(node.text);
  return vnodeText(node.children);
}

// ---------- Readable reply mentions ----------
// "@name" in the composer -> the full mention code for the post it came from
var vouchaMentionCodes = {};
function vouchaRestoreMentions(text) {
  var names = Object.keys(vouchaMentionCodes).sort(function (a, b) {
    return b.length - a.length;
  });
  names.forEach(function (readable) {
    var escaped = readable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Only a whole "@name" (start of text or after a space, newline or quote marker), never one
    // that's already inside a full mention code; followed by a space, punctuation or the end
    var pattern = new RegExp('(^|[\\s>(])' + escaped + '(?=$|[\\s.,!?;:)\\]])', 'g');
    text = text.replace(pattern, function (all, before) {
      return before + vouchaMentionCodes[readable];
    });
  });
  return text;
}

// ---------- Auto-loading feeds ----------
var VOUCHA_AUTOLOAD_MARGIN = 800; // start loading this many pixels before the bottom comes into view

function vouchaNearBottom(el) {
  var r = el.getBoundingClientRect();
  return r.top < (window.innerHeight || document.documentElement.clientHeight) + VOUCHA_AUTOLOAD_MARGIN;
}
function vouchaLoadMore(list) {
  var state = list.attrs && list.attrs.state;
  if (!state || !state.hasNext() || state.isLoadingNext() || state.isInitialLoading && state.isInitialLoading()) return;
  var done = function () {
    // A short page can leave the bottom still in view: check again rather than stall
    setTimeout(function () {
      if (list.__vouchaTarget && list.__vouchaTarget.isConnected && vouchaNearBottom(list.__vouchaTarget)) vouchaLoadMore(list);
    }, 60);
  };
  var p = state.loadNext();
  if (p && p.then) p.then(done, function () {/* the Load more button stays for a manual retry */});
}
function vouchaAutoLoad(list) {
  if (!list.element || typeof window.IntersectionObserver === 'undefined') return; // old browsers keep the button
  var target = list.element.querySelector('.DiscussionList-loadMore');
  if (!target || target === list.__vouchaTarget) return;
  if (list.__vouchaObserver) list.__vouchaObserver.disconnect();
  list.__vouchaTarget = target;
  list.__vouchaObserver = new window.IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) {
        vouchaLoadMore(list);
        return;
      }
    }
  }, {
    rootMargin: '0px 0px ' + VOUCHA_AUTOLOAD_MARGIN + 'px 0px'
  });
  list.__vouchaObserver.observe(target);
}

// ---------- Composer: formatting buttons Voucha doesn't use ----------
// Removed from the toolbar (Flarum markdown names). Runs after the markdown extension
// has added its buttons (low priority), so the removal sticks.
var VOUCHA_HIDDEN_FORMAT_BUTTONS = ['italic', 'strikethrough', 'code'];
app.initializers.add('voucha-theme-composer-buttons', function () {
  extend('flarum/common/components/TextEditor', 'markdownToolbarItems', function (items) {
    VOUCHA_HIDDEN_FORMAT_BUTTONS.forEach(function (key) {
      if (items && items.has && items.has(key)) items.remove(key);
    });
    // No image-link button: photos are added with the upload button
    if (items && items.has && items.has('image')) items.remove('image');
  });
  // "Add a video or post" (YouTube, X, Facebook), as in the app; in the composer only, not other editors
  extend('flarum/common/components/TextEditor', 'toolbarItems', function (items) {
    if (!this.attrs || !this.attrs.composer) return;
    items.add('vouchaEmbed', m(TextEditorButton, {
      onclick: openEmbedLink,
      icon: 'fab fa-youtube'
    }, 'Add a video or post (YouTube, X, Facebook)'), 5);
  });
}, -100);

// ---------- Composer: a live character counter for the post length limits ----------
// Opening posts and replies have limits (Admin > Voucha Theme); the server enforces them, and this
// counter shows them while writing: amber from 90%, red past the limit. Hidden for exempt accounts.
function vouchaPostLimit() {
  var user = app.session.user;
  if (!user) return null;
  var exempt = String(app.forum.attribute('vouchaLimitExempt') || '').toLowerCase().split(',').map(function (s) {
    return s.trim();
  });
  var isMod = (user.groups() || []).some(function (g) {
    return g && (String(g.id()) === '1' || String(g.id()) === '4');
  });
  if (isMod || exempt.indexOf(String(user.username()).toLowerCase()) !== -1) return null;
  var body = app.composer && app.composer.body;
  var attrs = body && body.attrs || {};
  var first = attrs.post ? attrs.post.number() === 1 : !attrs.discussion; // editing post #1, or a new discussion
  return {
    first: first,
    limit: first ? app.forum.attribute('vouchaLimitFirst') || 5000 : app.forum.attribute('vouchaLimitReply') || 2000
  };
}
app.initializers.add('voucha-theme-post-limit', function () {
  extend('flarum/common/components/TextEditor', 'toolbarItems', function (items) {
    var rule = vouchaPostLimit();
    if (!rule || !app.composer || !app.composer.fields || !app.composer.fields.content) return;
    var used = Array.from(String(app.composer.fields.content() || '').trim()).length; // characters, not bytes
    var tone = used > rule.limit ? 'is-over' : used >= rule.limit * 0.9 ? 'is-near' : '';
    items.add('vouchaCount', m('span', {
      className: 'VouchaCount ' + tone,
      title: (rule.first ? 'Opening posts' : 'Replies') + ' can be up to ' + rule.limit.toLocaleString('en-US') + ' characters',
      'aria-live': 'polite'
    }, used > rule.limit ? (used - rule.limit).toLocaleString('en-US') + ' too long (' + rule.limit.toLocaleString('en-US') + ' max)' : used.toLocaleString('en-US') + ' / ' + rule.limit.toLocaleString('en-US')), -1000);
  });
}, -100);

// ---------- Tag headers: background image (set in Admin > Tags) ----------
// The photo sits under a tint of the tag's own colour (or a dark tint), so the title and text stay readable.
function vouchaTint(hex) {
  var m6 = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m6) return 'rgba(20, 30, 28, 0.62)';
  var n = parseInt(m6[1], 16);
  return 'rgba(' + (n >> 16 & 255) + ', ' + (n >> 8 & 255) + ', ' + (n & 255) + ', 0.80)';
}
app.initializers.add('voucha-theme-tag-header-images', function () {
  override('ext:flarum/tags/forum/components/TagHero', 'style', function (original) {
    var base = original() || {};
    var tag = this.attrs.model;
    var img = tag && tag.attribute && tag.attribute('vouchaHeaderImage');
    if (!img) return base;
    var tint = vouchaTint(tag.color());
    return Object.assign({}, base, {
      backgroundImage: 'linear-gradient(' + tint + ', ' + tint + '), url("' + img + '")',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    });
  });
  extend('ext:flarum/tags/forum/components/TagHero', 'className', function (cls) {
    var tag = this.attrs.model;
    var img = tag && tag.attribute && tag.attribute('vouchaHeaderImage');
    return img ? cls + ' TagHero--image' + (tag.color() ? '' : ' TagHero--imageDark') : cls;
  });
});

// ---------- Welcome banner: three things to do on Voucha, and Join ----------
// Under the banner's own title and message: My Community, your state's ranking, and the Atlas;
// visitors also get "Join Voucha". The banner's dismiss button is unchanged.
var VOUCHA_WELCOME_CARDS = [{
  icon: 'fas fa-users',
  title: 'Join your community',
  short: 'Community',
  text: 'Nigerians near you and from home: news, questions and meetups.',
  href: '/community'
}, {
  icon: 'fas fa-chart-bar',
  title: 'Track your state',
  short: 'Your state',
  text: 'See how your state of origin ranks, and how its governor is doing.',
  href: '/community/governors'
}, {
  icon: 'fas fa-map-marked-alt',
  title: 'Explore the Voucha Atlas',
  short: 'Atlas',
  text: 'Map resources, industry and trade routes, and spot business opportunities.',
  href: '/atlas'
}];
app.initializers.add('voucha-theme-welcome-banner', function () {
  extend('flarum/forum/components/WelcomeHero', 'contentItems', function (items) {
    var go = function (href) {
      return function (e) {
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          m.route.set(href);
        }
      };
    };
    items.add('voucha-cards', m('div', {
      className: 'VouchaWelcome-cards'
    }, VOUCHA_WELCOME_CARDS.map(function (c) {
      return m('a', {
        className: 'VouchaWelcome-card',
        href: c.href,
        onclick: go(c.href)
      }, [m('i', {
        className: c.icon + ' VouchaWelcome-icon',
        'aria-hidden': 'true'
      }), m('strong', c.title), m('b', {
        className: 'VouchaWelcome-short',
        'aria-hidden': 'true'
      }, c.short),
      // phones: one row of three tiles
      m('span', c.text), m('i', {
        className: 'fas fa-chevron-right VouchaWelcome-arrow',
        'aria-hidden': 'true'
      })]);
    })), -10); // after the banner's own title (100) and message (0)
    // what's been happening, once the figures are worth showing
    var nd = app.forum.attribute('vouchaMonthDiscussions') || 0,
      nr = app.forum.attribute('vouchaMonthReplies') || 0;
    var bits = [];
    if (nd >= 30) bits.push(nd.toLocaleString() + ' new discussions');
    if (nr >= 30) bits.push(nr.toLocaleString() + ' replies');
    if (bits.length) items.add('voucha-activity', m('p', {
      className: 'VouchaWelcome-activity'
    }, [m('i', {
      className: 'fas fa-circle VouchaWelcome-live',
      'aria-hidden': 'true'
    }), bits.join(' and ') + ' in the last 30 days']), -5); // between the message and the cards
    if (!app.session.user) {
      items.add('voucha-join', m('div', {
        className: 'VouchaWelcome-actions'
      }, m('button', {
        type: 'button',
        className: 'Button Button--primary VouchaWelcome-btn',
        onclick: function () {
          try {
            var s = app.modal.show(function () {
              return flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal');
            });
            if (s && s.catch) s.catch(function () {});
          } catch (e) {}
        }
      }, 'Join Voucha')), -20);
    }
  });
});

// ---------- No poll badge on the avatar ----------
// FoF Polls adds a "poll" badge, which sits on the avatar in the feed. The card already shows the poll itself
// ("Poll · N participants"), so the badge only hides the author's picture. Runs late, after FoF Polls has added it;
// the CSS hides .Badge--poll as well, in case of a different order.
app.initializers.add('voucha-theme-no-poll-badge', function () {
  extend('flarum/common/models/Discussion', 'badges', function (items) {
    if (items.has('poll')) items.remove('poll');
  });
}, -100);

// ---------- Member badges: signed-in readers only ----------
// Badges on people (group badges such as the ID card, moderator and admin marks, and any other extension's)
// show on profiles, posts and avatars only to signed-in readers; visitors see names and pictures without them.
// Runs late, after the extensions that add badges.
app.initializers.add('voucha-theme-member-badges', function () {
  extend('flarum/common/models/User', 'badges', function (items) {
    if (app.session && app.session.user) return;
    Object.keys(items.toObject()).forEach(function (key) {
      items.remove(key);
    });
  });
}, -100);

// ---------- Written summaries for tool and data pages ----------
// app.vouchaPageText(path, className) returns the page's one- or two-sentence summary (worked out on the server from
// Voucha's data, see src/Seo/PageText.php) as a paragraph, or null. The first page load has it preloaded; after
// that it comes from /api/voucha-theme/page-text, once per address.
var vouchaPageTexts = {};
app.initializers.add('voucha-theme-page-text', function () {
  app.vouchaPageText = function (path, className) {
    if (!(path in vouchaPageTexts)) {
      var pre = app.data && app.data.vouchaPageText;
      if (pre && pre.path === path) {
        vouchaPageTexts[path] = pre.summary || null;
      } else {
        vouchaPageTexts[path] = null;
        app.request({
          method: 'GET',
          url: app.forum.attribute('apiUrl') + '/voucha-theme/page-text?path=' + encodeURIComponent(path),
          errorHandler: function () {}
        }).then(function (r) {
          vouchaPageTexts[path] = r && r.summary || null;
          m.redraw();
        }).catch(function () {});
      }
    }
    var text = vouchaPageTexts[path];
    return text ? m('p', {
      className: 'VouchaPageText-summary ' + (className || '')
    }, text) : null;
  };
});

// ---------- Home page: a band of live figures above the feed ----------
// The naira, inflation and the NGX, each linking to its page (src/Seo/HomeBand.php). For
// everyone, signed in or not, on the home page only. Preloaded on the first visit; fetched once after that.
var vouchaBandTiles = null;
var vouchaBandLoading = false;
function vouchaHomeBand() {
  if (vouchaBandTiles === null) {
    var pre = app.data && app.data.vouchaHomeBand;
    if (pre) {
      vouchaBandTiles = pre;
    } else if (!vouchaBandLoading) {
      vouchaBandLoading = true;
      app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/voucha-theme/home-band',
        errorHandler: function () {}
      }).then(function (r) {
        vouchaBandTiles = r && r.tiles || [];
        m.redraw();
      }).catch(function () {
        vouchaBandTiles = [];
      });
    }
  }
  if (!vouchaBandTiles || !vouchaBandTiles.length) return null;
  return m('nav', {
    className: 'VouchaBand',
    'aria-label': 'Today on Voucha'
  }, vouchaBandTiles.map(function (t) {
    return m('a', {
      className: 'VouchaBand-tile',
      href: t.href,
      onclick: function (e) {
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          m.route.set(t.href);
        }
      }
    }, [m('i', {
      className: t.icon + ' VouchaBand-icon',
      'aria-hidden': 'true'
    }), m('span', {
      className: 'VouchaBand-label'
    }, t.label), t.value ? m('strong', {
      className: 'VouchaBand-value'
    }, t.value) : null]);
  }));
}
app.initializers.add('voucha-theme-home-band', function () {
  extend(IndexPage.prototype, 'contentItems', function (items) {
    var path = window.location.pathname.replace(/\/+$/, '') || '/';
    var base = (app.forum.attribute('basePath') || '').replace(/\/+$/, '');
    if (base && path.indexOf(base) === 0) path = path.slice(base.length) || '/';
    if (path !== '/' || m.route.param('q')) return;
    var band = vouchaHomeBand();
    if (band) items.add('voucha-band', band, 110); // above the toolbar (100) and the list (90)
  });
});

// ---------- Site footer: fold / unfold a group of links on phones and iPads (src/Seo/SiteFooter.php) ----------
document.addEventListener('click', function (e) {
  var btn = e.target && e.target.closest && e.target.closest('.VouchaSiteFooter-toggle');
  if (!btn || window.innerWidth >= 1100) return;
  var col = btn.closest('.VouchaSiteFooter-col');
  if (!col) return;
  var open = !col.classList.contains('is-open');
  col.classList.toggle('is-open', open);
  btn.setAttribute('aria-expanded', open ? 'true' : 'false');
});

// ---------- Phones: name custom pages in the header ----------
// Flarum shows a title in the mobile header only on pages that render an .App-titleControl (lists,
// discussions, profiles). Pages without one (Resources, Data Hub, Atlas, News, Markets…) get a
// plain-text title, the same element Flarum's own text-title pages use.
var VOUCHA_PAGE_TITLES = [['/resources', 'Resources'], ['/data-hub', 'Data Hub'], ['/atlas', 'Voucha Atlas'], ['/resource-map', 'Voucha Atlas'], ['/news', 'News'], ['/markets', 'Markets'], ['/advertise', 'Advertise'], ['/advertiser', 'Advertiser'], ['/business', 'For Business'], ['/moderators', 'Moderators'], ['/calculators', 'Calculators'], ['/tools', 'Tools']];
function vouchaPageTitle() {
  var path = window.location.pathname.replace(/\/+$/, '') || '/';
  for (var i = 0; i < VOUCHA_PAGE_TITLES.length; i++) {
    var p = VOUCHA_PAGE_TITLES[i][0];
    if (path === p || path.indexOf(p + '/') === 0) return VOUCHA_PAGE_TITLES[i][1];
  }
  var t = String(app.title || '').trim();
  return t && t.length <= 30 ? t : null;
}
function vouchaMobileTitle() {
  var ours = document.querySelector('.Voucha-pageTitle');
  var native = document.querySelector('.App-titleControl:not(.Voucha-pageTitle)');
  var title = native ? null : vouchaPageTitle();
  if (!title) {
    if (ours) ours.remove();
    return;
  }
  var content = document.querySelector('.App-content') || document.getElementById('content');
  if (!content) return;
  if (!ours) {
    ours = document.createElement('h4');
    ours.className = 'App-titleControl App-titleControl--text Voucha-pageTitle';
    content.insertBefore(ours, content.firstChild);
  }
  if (ours.textContent !== title) ours.textContent = title;
}
app.initializers.add('voucha-theme-mobile-title', function () {
  extend(Page.prototype, 'oncreate', function () {
    setTimeout(vouchaMobileTitle, 0);
  });
  extend(Page.prototype, 'onupdate', function () {
    vouchaMobileTitle();
  });
});

// ---------- Phones: the composer fills the visible screen ----------
// Flarum 2 opens the phone composer as a 350px sheet, which leaves little room for the text once the title,
// tags, poll and image rows are in. The theme's CSS sizes it to --voucha-vv-height instead: the height of the
// part of the screen you can see, which shrinks when the keyboard opens. A window resize makes Flarum re-fit
// the text box to the new height.
function vouchaViewportHeight() {
  var vv = window.visualViewport;
  var h = vv ? vv.height : window.innerHeight;
  document.documentElement.style.setProperty('--voucha-vv-height', Math.round(h) + 'px');
}
app.initializers.add('voucha-theme-composer-height', function () {
  vouchaViewportHeight();
  var timer = null;
  var onChange = function () {
    vouchaViewportHeight();
    clearTimeout(timer);
    timer = setTimeout(function () {
      if (window.$) window.$(window).trigger('resize');
    }, 60);
  };
  if (window.visualViewport) window.visualViewport.addEventListener('resize', onChange);else window.addEventListener('resize', vouchaViewportHeight);
});

// ---------- A simpler index toolbar (every width) ----------
// Flarum shows refresh and mark-all-read as two unlabelled icons. Refresh is dropped (switching sort or
// reopening the page reloads the list) and "Mark all as read" moves into the sort menu with a label; on
// phones that also gives the search field the room.
app.initializers.add('voucha-theme-phone-toolbar', function () {
  extend(IndexPage.prototype, 'actionItems', function (items) {
    if (items.has('refresh')) items.remove('refresh');
    if (items.has('markAllAsRead')) items.remove('markAllAsRead');
  });
  extend(IndexPage.prototype, 'viewItems', function (items) {
    if (!app.session.user || !items.has('sort')) return;
    var sort = items.get('sort');
    if (!sort || !Array.isArray(sort.children)) return;
    var page = this;
    sort.children = sort.children.concat([m(Separator), m(Button, {
      icon: 'fas fa-check',
      onclick: function () {
        page.markAllAsRead();
      }
    }, app.translator.trans('core.forum.index.mark_all_as_read_tooltip'))]);
  });
});

// ---------- Phones: compact discussion rows ----------
// On phones the right-hand column of unlabelled count boxes is gone; replies and views sit on the meta line with
// icons, after the author and a short time ("1d"), as one unit that never splits across lines:
//   MoneyMatters · 1d · [comment] 0 · [eye] 9
// Tags show the first two, then "+N". Search results (which show an excerpt) are left as they are.
app.initializers.add('voucha-theme-phone-rows', function () {
  extend(DiscussionListItem.prototype, 'infoItems', function (items) {
    // added at every width; the theme's CSS shows it (and hides Flarum's line) only on phones
    if (this.attrs.params && this.attrs.params.q) return;
    var d = this.attrs.discussion;
    if (!d) return;

    // "+N" for the tags that don't fit on the line; vouchaFitTags() fills it in once the row is drawn
    var tags = (d.tags && d.tags() || []).filter(Boolean);
    if (tags.length > 1) items.add('voucha-tags-more', m('span.VouchaTagsMore'), 9);

    // like Flarum's own line: the last reply when there is one (marked with the reply arrow), else the starter
    var first = this.showFirstPost() || !d.replyCount();
    var user = first ? d.user() : d.lastPostedUser();
    var when = first ? d.createdAt() : d.lastPostedAt();
    var unread = !!(d.isUnread && d.isUnread());
    var replies = unread ? d.unreadCount() : d.replyCount();
    var views = viewCountOf(d);
    items.add('voucha-meta', m('span.VouchaMeta', [user ? m('span.VouchaMeta-user', [first ? null : m('i.fas.fa-reply', {
      'aria-hidden': 'true'
    }), ' ', user.displayName()]) : null, when ? m('time.VouchaMeta-time', {
      datetime: when.toISOString(),
      title: when.toLocaleString()
    }, relativeTime(when)) : null, m('span.VouchaMeta-count' + (unread ? '.is-unread' : ''), {
      'aria-label': unread ? replies + ' unread' : replies + (replies === 1 ? ' reply' : ' replies')
    }, [m('i.far.fa-comment', {
      'aria-hidden': 'true'
    }), ' ', formatCount(replies)]), views ? m('span.VouchaMeta-count', {
      'aria-label': views + (views === 1 ? ' view' : ' views')
    }, [m('i.far.fa-eye', {
      'aria-hidden': 'true'
    }), ' ', formatCount(views)]) : null]), -10);
  });
});

// Phones: every tag that fits on the row's tag line shows; the rest become "+N".
function vouchaFitTags(row) {
  var info = row && row.querySelector('.DiscussionListItem-info');
  var label = info && info.querySelector('.item-tags .TagsLabel');
  var moreLi = info && info.querySelector('.item-voucha-tags-more');
  if (!label || !moreLi) return;
  var tags = [].slice.call(label.children);
  tags.forEach(function (t) {
    t.classList.remove('is-tagHidden');
  });
  moreLi.classList.remove('is-shown');
  if (!window.matchMedia('(max-width: 1099px)').matches || tags.length < 2) return;
  var top = function (el) {
    return el.getBoundingClientRect().top;
  };
  var line = top(tags[0]);
  var onLine = function (el) {
    return Math.abs(top(el) - line) < 6;
  };
  var visible = function () {
    return tags.filter(function (t) {
      return !t.classList.contains('is-tagHidden');
    });
  };
  if (tags.every(onLine)) return;

  // some wrap: show "+N" and drop tags from the end until the tags and "+N" share one line
  moreLi.classList.add('is-shown');
  var chip = moreLi.querySelector('.VouchaTagsMore');
  for (var i = tags.length - 1; i > 0; i--) {
    tags[i].classList.add('is-tagHidden');
    var hidden = tags.length - visible().length;
    if (chip) chip.setAttribute('data-more', '+' + hidden);
    if (visible().every(onLine) && onLine(moreLi)) break;
  }
}
app.initializers.add('voucha-theme-phone-tags', function () {
  extend(DiscussionListItem.prototype, 'oncreate', function () {
    vouchaFitTags(this.element);
  });
  extend(DiscussionListItem.prototype, 'onupdate', function () {
    vouchaFitTags(this.element);
  });
  var timer = null;
  window.addEventListener('resize', function () {
    clearTimeout(timer);
    timer = setTimeout(function () {
      document.querySelectorAll('.DiscussionListItem').forEach(vouchaFitTags);
    }, 150);
  });
});

// ---------- Feed previews: plain sentences only ----------
// Synopsis builds each card's preview from the post's text, so tables ("|Currency|Rate| |--|--:|") and
// uploaded-image codes ("[upl-image-preview uuid=... url=...]") showed up as raw markup. Cleaned where the
// preview text is read: the discussion's synopsisExcerpt (plain previews) and a post's contentPlain.
function vouchaCleanExcerpt(text) {
  // a preview that runs into a table (CPI reports): just the sentence(s) before it (FeedTidy.js)
  var s = beforeTable(text) || String(text);
  s = s.replace(/!\[[^\]]*\]\([^)]*\)/g, ' '); // markdown images
  s = s.replace(/\[([^\]\n]+)\]\((?:https?:|\/)[^)\s]*\)/g, '$1'); // markdown links -> their text
  // video and social post links: unreadable in a preview (the card shows the video's picture instead)
  s = s.replace(/<?https?:\/\/(?:www\.|m\.|mobile\.|web\.)?(?:youtube\.com|youtu\.be|x\.com|twitter\.com|facebook\.com|fb\.watch)\/\S*/gi, ' ');
  // BBCode-style codes (uploaded images, url, quote...), also when the preview cut one off mid-way
  s = s.replace(/\[\/?(?:upl-[\w-]+|img|url|b|i|u|s|quote|code|size|color|center|spoiler|list|\*)\b[^\]]*(?:\]|$)/gi, ' ');
  s = s.replace(/\|?\s*:?-{2,}:?\s*(?:\|\s*:?-{2,}:?\s*)+\|?/g, ' '); // table rule rows |---|--:|
  s = s.replace(/\s*\|\s*/g, ' · '); // table cells
  s = s.replace(/(^|\s)#{1,6}\s+/g, '$1'); // heading marks
  s = s.replace(/(\*\*|__)([^*_]+?)\1/g, '$2'); // bold
  s = s.replace(/\s+/g, ' ').replace(/(?:\s*·\s*){2,}/g, ' · ').replace(/^\s*·\s*|\s*·\s*$/g, '');
  return s.trim();
}
var vouchaCleanCache = {};
function vouchaCleanCached(v) {
  if (typeof v !== 'string' || !v) return v;
  if (!(v in vouchaCleanCache)) vouchaCleanCache[v] = vouchaCleanExcerpt(v);
  return vouchaCleanCache[v];
}
app.initializers.add('voucha-theme-clean-excerpts', function () {
  override(Discussion.prototype, 'attribute', function (original, key) {
    var v = original(key);
    // synopsisExcerpt: FoF Synopsis's previews; firstPostExcerpt: flarum/sticky's, shown on pinned discussions
    return key === 'synopsisExcerpt' || key === 'firstPostExcerpt' ? vouchaCleanCached(v) : v;
  });
  override(Post.prototype, 'contentPlain', function (original) {
    return vouchaCleanCached(original());
  });
});

// ---------- Search hints: fewer filters for regular members ----------
// Flarum 2 lists (and applies) search filters per type. Members don't need these; admins keep all.
var VOUCHA_HIDDEN_GAMBITS = {
  discussions: ['author', 'created'],
  posts: ['discussion', 'mentioned'],
  users: ['email', 'group', 'sso']
};
app.initializers.add('voucha-theme-search-hints', function () {
  override('flarum/common/GambitManager', 'for', function (original, type) {
    var list = original(type);
    var user = app.session && app.session.user;
    var hidden = VOUCHA_HIDDEN_GAMBITS[type];
    if (!hidden || user && user.isAdmin && user.isAdmin()) return list;
    return list.filter(function (g) {
      var keys = [];
      try {
        keys.push(String(g.filterKey ? g.filterKey() : '').toLowerCase());
      } catch (e) {}
      try {
        keys.push(String(g.key ? g.key() : '').toLowerCase());
      } catch (e) {}
      return !keys.some(function (k) {
        return hidden.indexOf(k) !== -1;
      });
    });
  });
});

// ---------- The join prompt (signed-out visitors) ----------
// Shown at a moment of value, not on a timer alone: on tool pages a few seconds after the first
// result (or after a while if there's none), elsewhere after some reading time or half a page, and
// on discussions from the second one read. Never in the first 5 seconds or while typing; once per
// visit; after a close, not again for some days. A slide-in card (bottom sheet on phones), never a
// full-screen popup.
var VJ = {
  timers: [],
  observer: null,
  shown: false,
  path: null,
  scrollHandler: null
};
var VJ_TOOLS = ['/tax-calculator', '/salary-converter', '/business-tax', '/markets'];
var VJ_RESULT = '.VouchaCalculator-results, .VouchaCalculator-resultRow, .VouchaConv-big';
function vjSetting(key, fallback) {
  var v = parseInt(app.forum.attribute(key), 10);
  return isFinite(v) && v >= 0 ? v : fallback;
}
function vjStore(kind) {
  try {
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch (e) {
    return null;
  }
}
function vjContext(path) {
  if (VJ_TOOLS.some(function (p) {
    return path === p || path.indexOf(p + '/') === 0;
  })) return 'tools';
  if (/^\/d\//.test(path)) return 'discussion';
  return 'page';
}
function vjCopy(path, ctx) {
  if (ctx === 'tools') return ['Keep your numbers in one place', 'Join Voucha to save your calculations and compare notes with Nigerians at home and abroad.'];
  if (ctx === 'discussion') return ['Join the conversation', 'Reply, ask your own questions and join the communities that matter to you.'];
  if (path.indexOf('/community/governors') === 0 || path.indexOf('/community/kingdoms') === 0) return ['Track your state', 'Join Voucha and the communities for your state of origin and where you live, and see how they rank.'];
  if (path.indexOf('/atlas') === 0) return ['Map the opportunities', 'Join Voucha to save map plans, suggest places and discuss them with the community.'];
  return ['Join Nigerians near you and from home', 'Ask, share and follow what matters to you, with real figures and real people.'];
}
function vjAllowed() {
  if (app.session && app.session.user) return false;
  var s = vjStore('session');
  var l = vjStore('local');
  if (s && s.getItem('vjShown')) return false;
  var until = l ? parseInt(l.getItem('vjSnoozeUntil') || '0', 10) : 0;
  return !(until && Date.now() < until);
}
function vjTrack(event, ctx) {
  try {
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/voucha-theme/join-prompt',
      body: {
        event: event,
        context: ctx
      },
      errorHandler: function () {}
    }).catch(function () {});
  } catch (e) {}
}
function vjClear() {
  VJ.timers.forEach(clearTimeout);
  VJ.timers = [];
  if (VJ.observer) {
    VJ.observer.disconnect();
    VJ.observer = null;
  }
  if (VJ.scrollHandler) {
    window.removeEventListener('scroll', VJ.scrollHandler);
    VJ.scrollHandler = null;
  }
}
function vjTyping() {
  var el = document.activeElement;
  return el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
}
function vjShow(ctx) {
  if (VJ.shown || !vjAllowed()) return;
  if (document.querySelector('.VouchaGate')) return; // the reply wall already asks
  if (vjTyping() || document.querySelector('.Modal.fade.in, .ModalManager .Modal') || document.body.classList.contains('composerOpen')) {
    VJ.timers.push(setTimeout(function () {
      vjShow(ctx);
    }, 3000)); // not while typing or in a dialog
    return;
  }
  VJ.shown = true;
  var s = vjStore('session');
  if (s) s.setItem('vjShown', '1');
  var copy = vjCopy(window.location.pathname, ctx);
  var box = document.createElement('div');
  box.className = 'VouchaJoin-prompt';
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-label', copy[0]);
  box.innerHTML = '<button type="button" class="VouchaJoin-close" aria-label="Close">&times;</button>' + '<div class="VouchaJoin-head"><i class="fas fa-users" aria-hidden="true"></i><strong></strong></div><p></p>' + '<div class="VouchaJoin-actions"><button type="button" class="Button Button--primary VouchaJoin-join">Join Voucha</button>' + '<button type="button" class="Button Button--link VouchaJoin-login">Log in</button></div>';
  box.querySelector('strong').textContent = copy[0];
  box.querySelector('p').textContent = copy[1];
  // A fixed "Nigeria • time" pill (from the site's custom HTML) sits over the prompt's corner on
  // phones: hide it while the prompt is open, restore it afterwards
  var covered = [];
  Array.prototype.forEach.call(document.querySelectorAll('body div, body span, body a'), function (el) {
    if (covered.length > 3 || el.children.length > 3 || el === box || box.contains(el)) return;
    if (!/^\s*Nigeria\s*[•·]\s*\d/.test(el.textContent || '')) return;
    var pos = window.getComputedStyle(el).position;
    if (pos === 'fixed' || pos === 'sticky') {
      covered.push([el, el.style.visibility]);
      el.style.visibility = 'hidden';
    }
  });
  var close = function (snooze) {
    covered.forEach(function (c) {
      c[0].style.visibility = c[1];
    });
    box.classList.remove('is-in');
    setTimeout(function () {
      box.remove();
    }, 250);
    if (snooze) {
      var l = vjStore('local');
      if (l) l.setItem('vjSnoozeUntil', String(Date.now() + vjSetting('vouchaJoinSnoozeDays', 7) * 864e5));
    }
  };
  var open = function (which) {
    // literal paths: Flarum's build can only load dialogs by a fixed path
    var loader = which === 'login' ? function () {
      return flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal');
    } : function () {
      return flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal');
    };
    try {
      var r = app.modal.show(loader);
      if (r && r.catch) r.catch(function () {});
    } catch (e) {}
  };
  box.querySelector('.VouchaJoin-close').addEventListener('click', function () {
    vjTrack('closed', ctx);
    close(true);
  });
  box.querySelector('.VouchaJoin-join').addEventListener('click', function () {
    vjTrack('joined', ctx);
    close(false);
    open('signup');
  });
  box.querySelector('.VouchaJoin-login').addEventListener('click', function () {
    close(false);
    open('login');
  });
  document.body.appendChild(box);
  requestAnimationFrame(function () {
    box.classList.add('is-in');
  });
  vjTrack('shown', ctx);
}
function vjArm() {
  var path = window.location.pathname;
  if (path === VJ.path) return;
  VJ.path = path;
  vjClear();
  if (!vjAllowed() || VJ.shown) return;
  var ctx = vjContext(path);
  var at = function (sec) {
    VJ.timers.push(setTimeout(function () {
      vjShow(ctx);
    }, Math.max(5, sec) * 1000));
  };
  if (ctx === 'tools') {
    at(vjSetting('vouchaJoinToolsFallback', 45));
    var onResult = function () {
      if (!document.querySelector(VJ_RESULT)) return;
      if (VJ.observer) {
        VJ.observer.disconnect();
        VJ.observer = null;
      }
      at(vjSetting('vouchaJoinAfterResult', 3)); // the timer itself never fires before 5s on the page
    };
    VJ.observer = new MutationObserver(onResult);
    VJ.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  } else if (ctx === 'discussion') {
    var s = vjStore('session');
    var seen = s ? parseInt(s.getItem('vjDiscussions') || '0', 10) + 1 : 1;
    if (s) s.setItem('vjDiscussions', String(seen));
    if (seen >= vjSetting('vouchaJoinDiscussions', 2)) at(8);
  } else {
    at(vjSetting('vouchaJoinPages', 40));
    var started = Date.now();
    VJ.scrollHandler = function () {
      var depth = (window.scrollY + window.innerHeight) / Math.max(1, document.documentElement.scrollHeight);
      if (depth >= 0.5 && Date.now() - started > 5000) {
        vjClear();
        vjShow(ctx);
      }
    };
    window.addEventListener('scroll', VJ.scrollHandler, {
      passive: true
    });
  }
}
app.initializers.add('voucha-theme-join-prompt', function () {
  extend(Page.prototype, 'oncreate', function () {
    setTimeout(vjArm, 0);
  });
  extend(Page.prototype, 'onupdate', function () {
    vjArm();
  });
});

// ---------- Data pages: sign up to go further (signed-out visitors) ----------
// On the Data Hub home, the states list and Markets, a visitor who clicks a link in the page itself (a data
// theme, a latest update, a state...) to another Voucha page is asked to join or log in first; afterwards
// they land on the page they clicked. The header, menus and footer stay open, and links stay real links
// (search engines follow them as before).
var VD_PAGES = ['/data-hub', '/data-hub/states', '/markets', '/markets/ngx'];
var VD_OPEN_AREAS = '.App-header, .App-navigation, .IndexPage-nav, .sideNav, .Page-sidebar, .VouchaSiteFooter, footer, .Modal, .Alerts, .VouchaJoin-prompt';
function vdOnPage() {
  var p = window.location.pathname.replace(/\/+$/, '') || '/';
  return VD_PAGES.indexOf(p) !== -1;
}
function vdDestination(a) {
  var href = a.getAttribute('href');
  if (!href || href.charAt(0) === '#' || /^(mailto|tel|javascript):/i.test(href)) return null;
  if (a.target === '_blank' || a.hasAttribute('download')) return null;
  var url;
  try {
    url = new URL(href, window.location.href);
  } catch (e) {
    return null;
  }
  if (url.origin !== window.location.origin) return null; // other sites: not ours to gate
  if (url.pathname.replace(/\/+$/, '') === window.location.pathname.replace(/\/+$/, '')) return null; // same page (tabs, anchors)
  if (/^\/(advertise|business|privacy|terms|login|signup|register)/.test(url.pathname)) return null;
  return url.pathname + url.search + url.hash;
}
function vdGate(dest) {
  var s = vjStore('session');
  if (s) s.setItem('vdDest', dest);
  vjTrack('shown', 'data');
  app.vouchaDataGate = true; // SignUpModal shows the note below while this is set
  try {
    var r = app.modal.show(function () {
      return flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal');
    });
    if (r && r.catch) r.catch(function () {});
  } catch (e) {}
}
app.initializers.add('voucha-theme-data-gate', function () {
  // Google sign-in (fof/oauth): "Continue with Google" works for new and returning people alike
  // (now, and again once boot has finished, in case the locale arrives after the initializers)
  var continueWith = function () {
    try {
      var t = app.translator.translations || {};
      var add = {};
      Object.keys(t).forEach(function (k) {
        if (/^fof-oauth\.forum\.log_in\.with_(\w+_)?button$/.test(k) && /^Log\s+in\s+with/i.test(String(t[k]))) add[k] = String(t[k]).replace(/^Log\s+in\s+with/i, 'Continue with');
      });
      if (Object.keys(add).length) app.translator.addTranslations(add);
    } catch (e) {}
  };
  continueWith();
  setTimeout(continueWith, 0);
  document.addEventListener('click', function (e) {
    if (app.session && app.session.user) return;
    if (!vdOnPage() || e.defaultPrevented) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || a.closest(VD_OPEN_AREAS) || !a.closest('.App-content, #content, main')) return;
    var dest = vdDestination(a);
    if (!dest) return;
    e.preventDefault();
    e.stopImmediatePropagation(); // the page's own in-app navigation never runs
    vdGate(dest);
  }, true);

  // Log in: a warmer title, and "Remember me" ticked unless the opener said otherwise (shared computers untick it)
  override('flarum/forum/components/LogInModal', 'title', function () {
    return 'Welcome back';
  });
  extend('flarum/forum/components/LogInModal', 'oninit', function () {
    if (this.attrs.remember === undefined && this.remember) this.remember(true);
  });

  // The sign-up dialog says why it appeared: its own title, then the reason and what they get, above
  // the Google button and the form
  override('flarum/forum/components/SignUpModal', 'title', function (original) {
    return app.vouchaDataGate ? 'Keep exploring the data' : original();
  });
  extend('flarum/forum/components/SignUpModal', 'body', function (items) {
    if (!app.vouchaDataGate || !Array.isArray(items)) return;
    items.unshift(m('.VouchaDataGate-intro', [m('p.VouchaDataGate-note', 'Join Voucha to see state profiles, rankings, markets and the discussions behind them.'), m('ul.VouchaDataGate-perks', ['State rankings', 'Market data', 'Saved posts', 'Alerts'].map(function (t) {
      return m('li', t);
    }))]));
  });
  extend('flarum/forum/components/SignUpModal', 'onremove', function () {
    app.vouchaDataGate = false;
  });
  // logging in instead (from the dialog's link) also takes them on to the page they chose
  extend('flarum/forum/components/LogInModal', 'onremove', function () {
    app.vouchaDataGate = false;
  });

  // after joining or logging in (both reload the page): on to the page they clicked
  var s = vjStore('session');
  var dest = s && s.getItem('vdDest');
  if (dest && app.session && app.session.user) {
    s.removeItem('vdDest');
    vjTrack('joined', 'data');
    setTimeout(function () {
      try {
        m.route.set(dest);
      } catch (err) {
        window.location.href = dest;
      }
    }, 0);
  }
});

// ---------- Reply wall (signed-out visitors, Blind-style) ----------
// On a discussion, visitors read the opening post and the first few replies (setting, default 3).
// Later replies stay on the page but blurred, behind a "Join the conversation" card; only the first
// two blurred ones are drawn, so the wall doesn't run on for screens. Signed-in members never see it.
// The replies remain in the page for search engines; the blur is only visual.
var VG = {
  tracked: null
};
function vgLimit() {
  return vjSetting('vouchaReadGate', 3);
}
function vgOn() {
  return !(app.session && app.session.user) && /^\/d\//.test(window.location.pathname) && vgLimit() > 0;
}
// Blurred replies are made unreachable (no clicks, keyboard or screen reader), but only their own
// contents: the join card sits inside the first blurred reply and must stay clickable.
function vgMute(it, on) {
  Array.prototype.forEach.call(it.children, function (c) {
    if (c.classList.contains('VouchaGate')) return;
    c.inert = !!on;
    if (on) c.setAttribute('aria-hidden', 'true');else c.removeAttribute('aria-hidden');
  });
}
function vgClear() {
  document.querySelectorAll('.VouchaGate').forEach(function (el) {
    el.remove();
  });
  document.querySelectorAll('.VouchaGate-item').forEach(function (it) {
    it.classList.remove('VouchaGate-item', 'VouchaGate-hidden', 'VouchaGate-first');
    vgMute(it, false);
  });
  document.body.classList.remove('VouchaGate-on');
}
function vgCard(hiddenCount) {
  var card = document.createElement('div');
  card.className = 'VouchaGate';
  card.setAttribute('role', 'region');
  card.setAttribute('aria-label', 'Join the conversation');
  card.innerHTML = '<h3>Join the conversation on Voucha</h3><p></p>' + '<div class="VouchaGate-actions"><button type="button" class="Button Button--primary VouchaGate-join">Sign up</button>' + '<button type="button" class="Button VouchaGate-login">Log in</button></div>';
  card.querySelector('p').textContent = hiddenCount > 0 ? 'Sign up to read the other ' + hiddenCount + (hiddenCount === 1 ? ' reply' : ' replies') + ' and add your own, with Nigerians at home and across the diaspora.' : 'Sign up to read every reply and add your own, with Nigerians at home and across the diaspora.';
  var open = function (which) {
    var loader = which === 'login' ? function () {
      return flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal');
    } : function () {
      return flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal');
    };
    try {
      var r = app.modal.show(loader);
      if (r && r.catch) r.catch(function () {});
    } catch (e) {}
  };
  card.querySelector('.VouchaGate-join').addEventListener('click', function () {
    vjTrack('joined', 'gate');
    open('signup');
  });
  card.querySelector('.VouchaGate-login').addEventListener('click', function () {
    open('login');
  });
  return card;
}
function vouchaGate() {
  var stream = document.querySelector('.DiscussionPage .PostStream');
  if (!vgOn() || !stream) {
    if (document.querySelector('.VouchaGate, .VouchaGate-item')) vgClear();
    return;
  }
  var limit = vgLimit();
  var items = stream.querySelectorAll('.PostStream-item[data-index]');
  var first = null,
    shown = 0;
  items.forEach(function (it) {
    var idx = parseInt(it.getAttribute('data-index'), 10);
    var gated = isFinite(idx) && idx > limit; // index 0 is the opening post
    var isReplyBox = !!it.querySelector('.ReplyPlaceholder');
    if (!gated) {
      if (it.classList.contains('VouchaGate-item')) {
        it.classList.remove('VouchaGate-item', 'VouchaGate-hidden', 'VouchaGate-first');
        vgMute(it, false);
      }
      return;
    }
    it.classList.add('VouchaGate-item');
    vgMute(it, true);
    // the first two blurred replies are drawn (faded); the rest and the "Write a reply" box are not
    var hide = isReplyBox || shown >= 2;
    if (!hide) shown++;
    it.classList.toggle('VouchaGate-hidden', hide);
    it.classList.toggle('VouchaGate-first', !hide && !first);
    if (!hide && !first) first = it;
  });
  var card = document.querySelector('.VouchaGate');
  if (!first) {
    if (card) card.remove();
    document.body.classList.remove('VouchaGate-on');
    return;
  }
  document.body.classList.add('VouchaGate-on');
  if (!card || card.parentNode !== first) {
    if (card) card.remove();
    var d = app.current && app.current.get && app.current.get('discussion');
    var total = d && d.attribute ? (parseInt(d.attribute('commentCount'), 10) || 0) - 1 : 0; // replies
    first.insertBefore(vgCard(Math.max(0, total - limit)), first.firstChild);
    var path = window.location.pathname.split('/').slice(0, 3).join('/');
    if (VG.tracked !== path) {
      VG.tracked = path;
      vjTrack('shown', 'gate');
    }
  }
}
app.initializers.add('voucha-theme-reply-wall', function () {
  var soon = null;
  var tick = function () {
    try {
      vouchaGate();
    } catch (e) {}
  };
  var watched = null;
  setInterval(function () {
    var stream = document.querySelector('.DiscussionPage .PostStream');
    if (stream && stream !== watched && window.MutationObserver) {
      watched = stream;
      new MutationObserver(function () {
        if (soon) return;
        soon = setTimeout(function () {
          soon = null;
          tick();
        }, 60);
      }).observe(stream, {
        childList: true,
        subtree: true
      });
    }
    tick();
  }, 400);
});

// ---------- Comment box at the bottom of discussions (Blind-style) ----------
// Always there while reading: type and Post without opening the composer. "Full editor" moves the
// text into Flarum's composer (formatting, images, mentions). Signed-out visitors get the box too;
// tapping it opens sign-up. Hidden while the composer is open, and where the member can't reply
// (locked discussion, unconfirmed email). The box lives outside the page Mithril draws (fixed to the
// bottom of the window, lined up with the posts), so page redraws can't move or remove it.
var VC = {
  drafts: {},
  posting: false,
  pill: null,
  pillLooked: 0
};
function vcDiscussion() {
  var d = app.current && app.current.get && app.current.get('discussion');
  return d && d.id && /^\/d\//.test(window.location.pathname) ? d : null;
}
function vcLimit() {
  var user = app.session.user;
  if (!user) return 0;
  var exempt = String(app.forum.attribute('vouchaLimitExempt') || '').toLowerCase().split(',').map(function (s) {
    return s.trim();
  });
  var isMod = (user.groups() || []).some(function (g) {
    return g && (String(g.id()) === '1' || String(g.id()) === '4');
  });
  if (isMod || exempt.indexOf(String(user.username()).toLowerCase()) !== -1) return 0;
  return app.forum.attribute('vouchaLimitReply') || 2000;
}
function vcOpenModal(which) {
  var loader = which === 'login' ? function () {
    return flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal');
  } : function () {
    return flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal');
  };
  try {
    var r = app.modal.show(loader);
    if (r && r.catch) r.catch(function () {});
  } catch (e) {}
}
// The "Nigeria • time" pill (site custom HTML): hidden for guests on phones, where it covers the feed and
// the sign-up prompt (CSS: body.VouchaGuest .VouchaNgPill). It may load late, so look a few times.
app.initializers.add('voucha-theme-ng-pill', function () {
  if (app.session && app.session.user) return;
  document.body.classList.add('VouchaGuest');
  var find = function () {
    if (document.querySelector('.VouchaNgPill')) return;
    Array.prototype.some.call(document.querySelectorAll('body div, body span, body a'), function (el) {
      if (el.children.length > 3 || !/^\s*Nigeria\s*[•·]\s*\d/.test(el.textContent || '')) return false;
      var pos = window.getComputedStyle(el).position;
      if (pos === 'fixed' || pos === 'sticky') {
        el.classList.add('VouchaNgPill');
        return true;
      }
      return false;
    });
  };
  [300, 1500, 4000, 9000].forEach(function (ms) {
    setTimeout(find, ms);
  });
});
// The "Nigeria • time" pill (site custom HTML) sits in the bottom-left corner: lift it above the box
function vcLiftPill(px) {
  if (!VC.pill && Date.now() - (VC.pillLooked || 0) > 3000) {
    // look again now and then: it may load late
    VC.pillLooked = Date.now();
    Array.prototype.some.call(document.querySelectorAll('body div, body span, body a'), function (el) {
      if (el.children.length > 3 || !/^\s*Nigeria\s*[•·]\s*\d/.test(el.textContent || '')) return false;
      var pos = window.getComputedStyle(el).position;
      if (pos === 'fixed' || pos === 'sticky') {
        VC.pill = el;
        return true;
      }
      return false;
    });
  }
  if (VC.pill) VC.pill.style.transform = px ? 'translateY(-' + px + 'px)' : '';
}
function vcRemove() {
  var bar = document.querySelector('.VouchaCommentBar');
  if (bar) bar.remove();
  document.body.classList.remove('VouchaCommentBar-on');
  document.documentElement.style.removeProperty('--vc-bar-h');
  vcLiftPill(0);
}
function vcSize(ta) {
  ta.style.height = 'auto';
  ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
}
function vcBuild(d) {
  var guest = !(app.session && app.session.user);
  var bar = document.createElement('div');
  bar.className = 'VouchaCommentBar' + (guest ? ' is-guest' : '');
  bar.setAttribute('data-discussion', d.id());
  bar.innerHTML = '<div class="VouchaCommentBar-box">' + '<textarea rows="1" class="VouchaCommentBar-input" placeholder="Add a comment" aria-label="Add a comment"></textarea>' + '<div class="VouchaCommentBar-row">' + '<button type="button" class="VouchaCommentBar-full" title="Formatting, images and mentions"><i class="fas fa-expand-alt" aria-hidden="true"></i><span>Full editor</span></button>' + '<span class="VouchaCommentBar-count" aria-live="polite"></span>' + '<button type="button" class="Button Button--primary VouchaCommentBar-post">Post</button>' + '</div></div>';
  var ta = bar.querySelector('textarea');
  var post = bar.querySelector('.VouchaCommentBar-post');
  var count = bar.querySelector('.VouchaCommentBar-count');
  var limit = vcLimit();
  var refresh = function () {
    var text = ta.value.trim();
    var used = Array.from(text).length;
    var over = limit && used > limit;
    post.disabled = guest ? false : !text || over || VC.posting;
    count.textContent = limit && used >= limit * 0.9 ? over ? (used - limit).toLocaleString('en-US') + ' too long' : used.toLocaleString('en-US') + ' / ' + limit.toLocaleString('en-US') : '';
    count.className = 'VouchaCommentBar-count' + (over ? ' is-over' : limit && used >= limit * 0.9 ? ' is-near' : '');
    VC.drafts[d.id()] = ta.value;
    vcSize(ta);
  };
  if (guest) {
    ta.readOnly = true;
    var join = function (e) {
      if (e) e.preventDefault();
      ta.blur();
      vjTrack('joined', 'comment');
      vcOpenModal('signup');
    };
    ta.addEventListener('focus', join);
    post.addEventListener('click', join);
    bar.querySelector('.VouchaCommentBar-full').addEventListener('click', join);
  } else {
    ta.value = VC.drafts[d.id()] || '';
    ta.addEventListener('input', refresh);
    ta.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (!post.disabled) post.click();
      }
    });
    post.addEventListener('click', function () {
      var text = ta.value.trim();
      if (!text || VC.posting) return;
      VC.posting = true;
      post.textContent = 'Posting…';
      refresh();
      app.store.createRecord('posts').save({
        content: text,
        relationships: {
          discussion: d
        }
      }).then(function (p) {
        ta.value = '';
        VC.drafts[d.id()] = '';
        var stream = app.current.get('stream');
        if (stream && app.viewingDiscussion && app.viewingDiscussion(d)) stream.update().then(function () {
          stream.goToNumber(p.number());
        });
      }).catch(function () {/* Flarum shows the reason (too long, too fast, ...) */}).then(function () {
        VC.posting = false;
        post.textContent = 'Post';
        refresh();
      });
    });
    bar.querySelector('.VouchaCommentBar-full').addEventListener('click', function () {
      var text = ta.value;
      DiscussionControls.replyAction.call(d, true, false).then(function () {
        return app.composer.editorReady ? app.composer.editorReady() : null;
      }).then(function () {
        if (!text.trim()) return;
        if (app.composer.editor && app.composer.editor.insertAtCursor) app.composer.editor.insertAtCursor(text, false);else app.composer.fields.content(text);
        ta.value = '';
        VC.drafts[d.id()] = '';
        m.redraw();
      }).catch(function () {});
    });
  }
  setTimeout(refresh, 0);
  return bar;
}
function vouchaCommentBar() {
  var d = vcDiscussion();
  var stream = document.querySelector('.DiscussionPage .DiscussionPage-stream') || document.querySelector('.DiscussionPage .PostStream');
  var guest = !(app.session && app.session.user);
  var composing = app.composer && app.composer.isVisible && app.composer.isVisible();
  var can = d && (guest || d.canReply && d.canReply());
  if (!d || !stream || !can || composing || app.forum.attribute('vouchaCommentBox') === false) {
    vcRemove();
    return;
  }
  var bar = document.querySelector('.VouchaCommentBar');
  if (bar && (bar.getAttribute('data-discussion') !== String(d.id()) || bar.classList.contains('is-guest') !== guest)) {
    bar.remove();
    bar = null;
  }
  if (!bar) {
    bar = vcBuild(d);
    document.body.appendChild(bar);
    document.body.classList.add('VouchaCommentBar-on');
  }
  // line up with the posts column
  var r = stream.getBoundingClientRect();
  var phone = window.innerWidth < 768;
  bar.style.left = (phone ? 0 : Math.round(r.left)) + 'px';
  bar.style.width = (phone ? window.innerWidth : Math.round(r.width)) + 'px';
  var h = bar.offsetHeight;
  document.documentElement.style.setProperty('--vc-bar-h', h + 'px');
  // Pinned to the bottom of the window while reading; once the posts end, it rests in the room kept for it
  // under them (under the white card on desktop post pages, at the end of the posts elsewhere) and scrolls
  // up with the page, so it never sits over the footer.
  var card = document.body.classList.contains('voucha-post') ? stream.closest('.Page-content') : null;
  var posts = document.querySelector('.DiscussionPage .PostStream') || stream;
  var restAt = card ? card.getBoundingClientRect().bottom + 12 + h : posts.getBoundingClientRect().bottom;
  var lift = Math.max(0, Math.round(window.innerHeight - restAt));
  bar.style.bottom = lift + 'px';
  // resting under the posts: no white fade behind it (that's only there to hide text scrolling underneath)
  bar.classList.toggle('is-resting', lift > 0);
  vcLiftPill(h + lift);
}
app.initializers.add('voucha-theme-comment-bar', function () {
  var tick = function () {
    try {
      vouchaCommentBar();
    } catch (e) {}
  };
  setInterval(tick, 300);
  window.addEventListener('resize', tick);
  // follow the page as it scrolls, so the box settles under the posts without lagging behind
  var queued = false;
  window.addEventListener('scroll', function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      tick();
    });
  }, {
    passive: true
  });
});

// ---------- Phones: the header's page switcher on tag pages ----------
// It shows the active item's name (the tag), repeating the tag page's own title. Pages can ask for
// another label with app.current 'vouchaTitleLabel' (tag pages: "Discussions"; My Community sets its own).
app.initializers.add('voucha-theme-title-label', function () {
  extend(IndexPage.prototype, 'oninit', function () {
    var tags = null;
    try {
      tags = m.route.param('tags');
    } catch (e) {}
    if (tags && app.current) app.current.set('vouchaTitleLabel', 'Discussions');
  });
  override('flarum/common/components/SelectDropdown', 'getButtonContent', function (original, children) {
    var out = original(children);
    var label = app.current && app.current.get && app.current.get('vouchaTitleLabel');
    if (!label || String(this.attrs.className || '').indexOf('App-titleControl') === -1) return out;
    return out.map(function (c) {
      return c && c.attrs && String(c.attrs.className || '').indexOf('Button-label') !== -1 ? m('span', {
        className: 'Button-label'
      }, m('span', {
        className: 'Button-labelText'
      }, label)) : c;
    });
  });
});

// ---------- Composer: images as short labels with thumbnails ----------
// See ComposerImages.js. Watches the plain text box (not the visual editor), shortens image code to
// "[📷 Image N]", shows thumbnails under the box, and swaps labels back when posting.
function vouchaImageEditor() {
  return document.querySelector('.Composer textarea.TextEditor-editor');
}
function vouchaRenderThumbs(el) {
  // inside the text area's own box, along its bottom edge (Flarum sizes the text area to fill the
  // composer, so anything placed below it ends up outside the composer)
  var container = el.parentNode;
  var strip = container.querySelector('.VouchaComposerImages');
  var labels = ComposerImages.labelsIn(el.value);
  var relayout = function () {
    try {
      window.dispatchEvent(new Event('resize'));
    } catch (e) {}
  }; // Flarum re-sizes the text box
  if (!labels.length) {
    if (strip) {
      strip.remove();
      relayout();
    }
    container.classList.remove('VouchaComposerImages-host');
    return;
  }
  container.classList.add('VouchaComposerImages-host');
  if (!strip) {
    strip = document.createElement('div');
    strip.className = 'VouchaComposerImages';
    container.insertBefore(strip, el); // above the text (phones); placed along the bottom by CSS on desktop
    setTimeout(relayout, 0);
  }
  var key = labels.join(',');
  if (strip.getAttribute('data-key') === key) return;
  strip.setAttribute('data-key', key);
  strip.innerHTML = '';
  labels.forEach(function (n) {
    var img = ComposerImages.state.images[n];
    var item = document.createElement('div');
    item.className = 'VouchaComposerImages-item';
    var pic = document.createElement('img');
    pic.src = img.thumb;
    pic.alt = 'Image ' + n;
    pic.loading = 'lazy';
    var cap = document.createElement('span');
    cap.textContent = 'Image ' + n;
    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'VouchaComposerImages-remove';
    x.setAttribute('aria-label', 'Remove image ' + n);
    x.textContent = '×';
    x.addEventListener('click', function () {
      el.value = ComposerImages.removeLabel(el.value, n);
      el.dispatchEvent(new Event('input', {
        bubbles: true
      }));
      vouchaRenderThumbs(el);
    });
    item.appendChild(pic);
    item.appendChild(cap);
    item.appendChild(x);
    strip.appendChild(item);
  });
}
function vouchaImageTick() {
  var el = vouchaImageEditor();
  if (!el) return;
  if (el !== vouchaImageTick.el) {
    vouchaImageTick.el = el;
    ComposerImages.reset();
  } // a new composer
  var value = el.value;
  if (value.indexOf('[upl-image') !== -1 || value.indexOf('![') !== -1) {
    var cursor = el.selectionStart;
    var r = ComposerImages.shorten(value, cursor);
    if (r.text !== value) {
      el.value = r.text;
      try {
        el.setSelectionRange(Math.max(0, cursor - r.shift), Math.max(0, cursor - r.shift));
      } catch (e) {}
      el.dispatchEvent(new Event('input', {
        bubbles: true
      }));
    }
  }
  vouchaRenderThumbs(el);
}
app.initializers.add('voucha-theme-composer-images', function () {
  setInterval(vouchaImageTick, 400); // uploads insert text without a typing event, so watch briefly
  ['flarum/forum/components/DiscussionComposer', 'flarum/forum/components/ReplyComposer', 'flarum/forum/components/EditPostComposer'].forEach(function (path) {
    extend(path, 'data', function (data) {
      if (data && typeof data.content === 'string') data.content = ComposerImages.expand(data.content);
    });
  });
});

// ---------- "Follow us on Google" (Google's Preferred Sources), beside Share on discussion pages ----------
var VOUCHA_GOOGLE_G = '<svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"/><path fill="#FBBC05" d="M10.5 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.6 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.7-6c-2.1 1.4-4.9 2.3-8.2 2.3-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z"/></svg>';

// ---------- "Follow Voucha on WhatsApp": the channel opens in WhatsApp, where people tap Follow ----------
// (WhatsApp doesn't let a site follow a channel on someone's behalf; the Follow tap is always theirs)
var VOUCHA_WA_ICON = '<svg width="26" height="26" viewBox="0 0 32 32" aria-hidden="true"><path fill="#fff" d="M16 3C8.8 3 3 8.7 3 15.8c0 2.5.7 4.9 2 7L3 29l6.4-2c2 1.1 4.3 1.7 6.6 1.7 7.2 0 13-5.7 13-12.8C29 8.7 23.2 3 16 3zm0 23.4c-2.1 0-4.1-.6-5.9-1.7l-.4-.3-3.8 1.2 1.2-3.7-.3-.4c-1.2-1.8-1.8-3.8-1.8-5.9C5 9.9 9.9 5.2 16 5.2s11 4.7 11 10.6S22.1 26.4 16 26.4zm6-7.9c-.3-.2-2-1-2.3-1.1-.3-.1-.5-.2-.8.2-.2.3-.9 1.1-1.1 1.3-.2.2-.4.2-.7.1-.3-.2-1.4-.5-2.7-1.6-1-.9-1.7-2-1.9-2.3-.2-.3 0-.5.1-.7l.5-.6c.2-.2.2-.3.3-.6.1-.2 0-.4 0-.6l-1-2.5c-.3-.7-.6-.6-.8-.6h-.7c-.2 0-.6.1-.9.4-.3.3-1.2 1.1-1.2 2.8s1.2 3.2 1.4 3.5c.2.2 2.4 3.7 5.9 5.1 3.5 1.4 3.5.9 4.1.9.6-.1 2-.8 2.3-1.6.3-.8.3-1.5.2-1.6-.1-.2-.3-.3-.7-.4z"/></svg>';

// "Follow us" links beside Share: Google (preferred source) and the WhatsApp channel, as one compact group
// (side by side on phones, stacked on desktop). Each link is a setting; an empty one is left out.
var VOUCHA_WA_SMALL = '<svg width="18" height="18" viewBox="0 0 32 32" aria-hidden="true"><path fill="currentColor" d="M16 3C8.8 3 3 8.7 3 15.8c0 2.5.7 4.9 2 7L3 29l6.4-2c2 1.1 4.3 1.7 6.6 1.7 7.2 0 13-5.7 13-12.8C29 8.7 23.2 3 16 3zm0 23.4c-2.1 0-4.1-.6-5.9-1.7l-.4-.3-3.8 1.2 1.2-3.7-.3-.4c-1.2-1.8-1.8-3.8-1.8-5.9C5 9.9 9.9 5.2 16 5.2s11 4.7 11 10.6S22.1 26.4 16 26.4zm6-7.9c-.3-.2-2-1-2.3-1.1-.3-.1-.5-.2-.8.2-.2.3-.9 1.1-1.1 1.3-.2.2-.4.2-.7.1-.3-.2-1.4-.5-2.7-1.6-1-.9-1.7-2-1.9-2.3-.2-.3 0-.5.1-.7l.5-.6c.2-.2.2-.3.3-.6.1-.2 0-.4 0-.6l-1-2.5c-.3-.7-.6-.6-.8-.6h-.7c-.2 0-.6.1-.9.4-.3.3-1.2 1.1-1.2 2.8s1.2 3.2 1.4 3.5c.2.2 2.4 3.7 5.9 5.1 3.5 1.4 3.5.9 4.1.9.6-.1 2-.8 2.3-1.6.3-.8.3-1.5.2-1.6-.1-.2-.3-.3-.7-.4z"/></svg>';
function vouchaFollowLinks() {
  var google = String(app.forum.attribute('vouchaGoogleFollowUrl') || '').trim();
  var wa = String(app.forum.attribute('vouchaWhatsappChannelUrl') || '').trim();
  if (!google && !wa) return null;
  return m('.VouchaFollowLinks', [google ? m('a.Button.VouchaFollowLink.is-google', {
    href: google,
    target: '_blank',
    rel: 'noopener',
    title: 'Add Voucha as a preferred source on Google'
  }, [m.trust(VOUCHA_GOOGLE_G), m('span.VouchaFollowLink-long', 'Follow us on Google'), m('span.VouchaFollowLink-short', 'Google')]) : null, wa ? m('a.Button.VouchaFollowLink.is-whatsapp', {
    href: wa,
    target: '_blank',
    rel: 'noopener',
    title: 'Opens our channel in WhatsApp, where you tap Follow',
    'aria-label': 'Follow Voucha on WhatsApp'
  }, [m.trust(VOUCHA_WA_SMALL), m('span.VouchaFollowLink-long', 'Follow on WhatsApp'), m('span.VouchaFollowLink-short', 'WhatsApp')]) : null]);
}
app.initializers.add('voucha-theme-follow-links', function () {
  extend('flarum/forum/components/DiscussionPage', 'sidebarItems', function (items) {
    if (!this.discussion) return;
    var links = vouchaFollowLinks();
    if (links) items.add('vouchaFollowLinks', links, -50);
  });
});

// ---------- Ad slots: "Advertise here" when empty, a right column on tool pages, an optional left slot ----------
function vouchaAdPlaceholder(el) {
  if (!app.forum.attribute('vouchaAdsPlaceholder')) return;
  el.innerHTML = '<a class="VouchaAdOpen" href="/advertise">' + '<span class="VouchaAdOpen-label">Ad space available</span>' + '<strong>Reach Nigerians at home and abroad</strong>' + '<span class="VouchaAdOpen-text">Your brand here, beside the tools and data our readers use every day.</span>' + '<span class="VouchaAdOpen-cta">Advertise on Voucha <i class="fas fa-arrow-right" aria-hidden="true"></i></span>' + '</a>';
}
var VOUCHA_TOOL_ROUTES = /^\/(tax-calculator|salary-converter|business-tax|markets|data-hub(?!\/editor))(\/|$)/;
// Data Hub pages laid out edge to edge (state profiles, location guides, businesses): no side ads in the
// margins (they sat over the page's own map and columns); the ad below the page stays
var VOUCHA_NO_SIDE_ADS = /^\/data-hub\/(states|guides|businesses)(\/|$)/;
var vouchaRailRoute = null;

/** The left and right edges of what's actually drawn inside an element (visible children, a few levels deep) */
function vouchaContentEdges(el, depth) {
  var left = Infinity,
    right = 0;
  for (var i = 0; i < el.children.length; i++) {
    var c = el.children[i];
    if (c.classList && (c.classList.contains('VouchaToolSky') || c.classList.contains('VouchaToolInline'))) continue;
    var r = c.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) continue;
    var style = window.getComputedStyle(c);
    // a block that only spans the width (no background or border of its own) is measured by its contents
    var plain = style.backgroundColor === 'rgba(0, 0, 0, 0)' && style.borderRightWidth === '0px' && style.boxShadow === 'none';
    var e = depth > 0 && plain && c.children.length ? vouchaContentEdges(c, depth - 1) : null;
    if (e && e.right > 0) {
      left = Math.min(left, e.left);
      right = Math.max(right, e.right);
    } else {
      left = Math.min(left, r.left);
      right = Math.max(right, r.right);
    }
  }
  return {
    left: left === Infinity ? 0 : left,
    right: right
  };
}
var VOUCHA_SKY_W = 160;
var vouchaRailShape = '';
var vouchaRailShapeAt = 0;
var vouchaSkyRoutes = {};

/**
 * Tool pages (calculators, Markets, Data Hub): the tool keeps its width; slim ads sit in the empty
 * margins on either side when a margin is wide enough, and one slot always sits below the tool
 * (the only one on phones).
 */
function vouchaToolRail() {
  var on = app.forum.attribute('vouchaAdsToolRail') && app.vouchaAds && app.vouchaAds.fillSlot && VOUCHA_TOOL_ROUTES.test(window.location.pathname);
  var content = on ? document.querySelector('.App-content') : null;
  var route = window.location.pathname;
  var tool = route.split('/')[1] || '';
  var fill = function (slot, key) {
    if (vouchaSkyRoutes[key] === route && slot._vouchaFill) return;
    vouchaSkyRoutes[key] = route;
    app.vouchaAds.fillSlot(slot, {
      placement: 'banner',
      tool: tool,
      empty: vouchaAdPlaceholder
    });
  };
  var drop = function (sel, key) {
    var el = document.querySelector(sel);
    if (el) el.remove();
    vouchaSkyRoutes[key] = null;
  };
  if (!content) {
    drop('.VouchaToolSky.is-left', 'left');
    drop('.VouchaToolSky.is-right', 'right');
    drop('.VouchaToolInline', 'below');
    return;
  }

  // Content first: no ads until the page has drawn and stopped changing. The data loads after
  // the page opens, so measuring too early saw a half-empty page with wide "margins" and put the
  // side ads where the chart then appeared (and the bottom ad briefly sat near the top).
  var root = document.getElementById('content');
  var edges = vouchaContentEdges(content, 4);
  // This only gates the first appearance on a page. Once placed, the ads just follow the layout
  // below (moved, or removed if there's no room), so a spinner on a button doesn't reload them.
  var placed = vouchaSkyRoutes.left === route || vouchaSkyRoutes.right === route || vouchaSkyRoutes.below === route;
  var loading = false;
  if (root) {
    var spinners = root.querySelectorAll('.LoadingIndicator, .VouchaMarkets-loading');
    for (var i = 0; i < spinners.length; i++) if (spinners[i].offsetParent !== null) {
      loading = true;
      break;
    }
  }
  // settled = the page's width and position haven't changed for half a second
  var shape = route + '|' + Math.round(edges.left) + '|' + Math.round(edges.right) + '|' + window.innerWidth;
  if (shape !== vouchaRailShape) {
    vouchaRailShape = shape;
    vouchaRailShapeAt = Date.now();
  }
  var settled = Date.now() - vouchaRailShapeAt >= 500;
  if (!root || !placed && (loading || root.getBoundingClientRect().height < 300 || !settled)) {
    drop('.VouchaToolSky.is-left', 'left');
    drop('.VouchaToolSky.is-right', 'right');
    // the bottom ad waits too, unless it's already in place for this page
    if (vouchaSkyRoutes.below !== route) drop('.VouchaToolInline', 'below');
    return;
  }

  // the slim side ads, each centred in its margin
  var gutters = {
    left: edges.left,
    right: window.innerWidth - edges.right
  };
  ['left', 'right'].forEach(function (side) {
    var sel = '.VouchaToolSky.is-' + side;
    var room = gutters[side];
    if (!edges.right || room < VOUCHA_SKY_W + 32 || VOUCHA_NO_SIDE_ADS.test(route)) return drop(sel, side);
    var sky = document.querySelector(sel);
    if (!sky) {
      sky = document.createElement('aside');
      sky.className = 'VouchaToolSky is-' + side;
      sky.setAttribute('aria-label', 'Sponsored');
      document.body.appendChild(sky);
      vouchaSkyRoutes[side] = null;
    }
    var x = side === 'left' ? (room - VOUCHA_SKY_W) / 2 : edges.right + (room - VOUCHA_SKY_W) / 2;
    sky.style.left = Math.round(x) + 'px';
    fill(sky, side);
  });

  // below the tool: always (on phones it's the only one). It goes right after #content, outside
  // the part of the page Mithril draws: placed inside it, Mithril would add the page's own blocks
  // after it (ad on top after moving between pages) or it would land in the first box it found
  // (the Data Hub's search banner).
  if (!root.parentNode) return;
  var inline = document.querySelector('.VouchaToolInline');
  if (!inline || inline.previousElementSibling !== root) {
    if (inline) inline.remove();
    inline = document.createElement('aside');
    inline.className = 'VouchaToolInline';
    inline.setAttribute('aria-label', 'Sponsored');
    root.parentNode.insertBefore(inline, root.nextSibling);
    vouchaSkyRoutes.below = null;
  }
  fill(inline, 'below');
}

// The moment the address changes, the old page's ads go (they were placed for the old layout);
// the new page gets its own once it has loaded and settled.
function vouchaClearToolAds() {
  ['.VouchaToolSky', '.VouchaToolInline'].forEach(function (sel) {
    document.querySelectorAll(sel).forEach(function (el) {
      el.remove();
    });
  });
  vouchaSkyRoutes = {};
  vouchaRailShape = '';
}
app.initializers.add('voucha-theme-ad-rails', function () {
  var tick = function () {
    try {
      vouchaToolRail();
    } catch (e) {}
  };
  var soon = null;
  var tickSoon = function () {
    if (soon) return;
    soon = setTimeout(function () {
      soon = null;
      tick();
    }, 120);
  };
  var lastPath = window.location.pathname;
  var onNav = function () {
    if (window.location.pathname === lastPath) return;
    lastPath = window.location.pathname;
    vouchaClearToolAds();
    tickSoon();
  };
  ['pushState', 'replaceState'].forEach(function (fn) {
    var orig = history[fn];
    history[fn] = function () {
      var r = orig.apply(this, arguments);
      try {
        onNav();
      } catch (e) {}
      return r;
    };
  });
  window.addEventListener('popstate', onNav);

  // react as the page draws instead of waiting for the next timer check
  var watchContent = function () {
    var root = document.getElementById('content');
    if (!root || root._vouchaAdWatch || !window.MutationObserver) return;
    root._vouchaAdWatch = new MutationObserver(tickSoon);
    root._vouchaAdWatch.observe(root, {
      childList: true,
      subtree: true
    });
  };
  setInterval(function () {
    watchContent();
    tick();
  }, 400); // also catches layout shifts
  window.addEventListener('resize', tick);
});

// ---------- Profile header: FoF Gamification's rank badges in the right place ----------
// Gamification (2.0.0-beta.5, the latest) still looks for the badge list where Flarum 1 had it, directly
// in .UserCard-profile. Flarum 2 keeps badges inside .UserCard-content, so Gamification adds its own list
// to .UserCard-profile, which is a two-column grid (photo | content): the extra item pushed the name,
// details and bio into the narrow photo column. Move its ranks into the content's badge list (or next
// to the name). Registered late so this runs after Gamification's own change to the card.
app.initializers.add('voucha-theme-usercard-ranks', function () {
  var hasClass = function (node, cls) {
    return !!(node && node.attrs && node.attrs.className && String(node.attrs.className).split(' ').indexOf(cls) !== -1);
  };
  var findDeep = function (node, cls) {
    if (!node || !Array.isArray(node.children)) return null;
    for (var i = 0; i < node.children.length; i++) {
      var c = node.children[i];
      if (hasClass(c, cls)) return c;
      var d = findDeep(c, cls);
      if (d) return d;
    }
    return null;
  };
  extend('flarum/forum/components/UserCard', 'view', function (vnode) {
    var profile = findDeep(vnode, 'UserCard-profile');
    if (!profile || !Array.isArray(profile.children)) return;
    var strayIndex = -1;
    profile.children.forEach(function (c, i) {
      if (strayIndex === -1 && hasClass(c, 'UserCard-badges')) strayIndex = i;
    });
    if (strayIndex === -1) return;
    var stray = profile.children.splice(strayIndex, 1)[0];
    var content = findDeep(profile, 'UserCard-content');
    if (!content || !Array.isArray(content.children)) {
      profile.children.splice(strayIndex, 0, stray); // nowhere better: leave it as it was
      return;
    }
    var ranks = (Array.isArray(stray.children) ? stray.children : []).filter(Boolean);
    var badges = null;
    content.children.forEach(function (c) {
      if (!badges && hasClass(c, 'UserCard-badges')) badges = c;
    });
    // A list's items must all have keys or none (Flarum's badges have keys; Gamification's ranks don't)
    var keyLike = function (list, nodes, prefix) {
      var keyed = list.some(function (c) {
        return c && c.key != null;
      });
      nodes.forEach(function (n, i) {
        if (!n || typeof n !== 'object') return;
        var k = keyed ? prefix + i : undefined;
        n.key = k;
        if (n.attrs) n.attrs.key = k;
      });
    };
    if (badges && Array.isArray(badges.children)) {
      keyLike(badges.children, ranks, 'vouchaRank');
      badges.children = badges.children.concat(ranks);
    } else {
      var at = 0;
      content.children.forEach(function (c, i) {
        if (hasClass(c, 'UserCard-identity')) at = i + 1;
      });
      keyLike(content.children, [stray], 'vouchaRanks');
      content.children.splice(at, 0, stray);
    }
  });
}, -100);

// ---------- Cookie banner (see CookieConsent.js) ----------
app.initializers.add('voucha-theme-cookie-consent', function () {
  vouchaCookieConsent();
  vouchaTurnstile();
  vouchaPostClamp();
  vouchaReplyClamp();
});

// ---------- Tag pages: Join under the name, Community highlights, About card (see TagAbout.js) ----------
app.initializers.add('voucha-theme-tag-about', function () {
  extend('ext:flarum/tags/forum/components/TagHero', 'contentItems', function (items) {
    var tag = this.attrs.model;
    if (!tag || !tag.slug()) return;
    items.add('voucha-actions', heroActions(tag), 60);
    // the description moves to the About card (its default text), so the header is just name and Join
    if (items.has('tag-subtitle')) items.remove('tag-subtitle');
  });

  // Above the feed: the About block (phones and tablets only; desktop has it in the right column),
  // then the pinned discussions as highlight cards
  extend(IndexPage.prototype, 'contentItems', function (items) {
    var slug = currentTagSlug();
    if (!slug) return;
    items.add('voucha-about', m(TagAboutCard, {
      slug: slug,
      variant: 'inline',
      extra: function () {
        return sportsTableGroup(slug);
      }
    }), 120);
    // Sports and its sub-tags: football results and fixtures (Sports.js; hidden until there is data)
    if (isSportsTag(slug)) items.add('voucha-scores', m(SportsScores), 115);
    items.add('voucha-highlights', m(TagHighlights, {
      slug: slug
    }), 110);
  });

  // Pinned discussions shown as highlights are left out of the feed below them (by class here when the
  // highlights are already loaded; by the style rule TagHighlights draws, which matches data-voucha-id, otherwise)
  extend(DiscussionListItem.prototype, 'elementAttrs', function (attrs) {
    var d = this.attrs.discussion;
    if (d) attrs['data-voucha-id'] = String(d.id());
    if (!d || !d.isSticky || !d.isSticky()) return;
    if (highlightIds(currentTagSlug()).indexOf(String(d.id())) !== -1) attrs.className = (attrs.className || '') + ' is-vouchaHighlight';
  });
});

// ---------- Feed cards: a small picture on the right ----------
// News posts: voucha-feed's vouchaThumb, a 240px copy of the card image. Otherwise, a post with a YouTube video
// (voucha-feed's vouchaVideo, its id) shows the video's own thumbnail with a play mark. Loaded only as it
// scrolls into view; posts with neither keep the full width (no placeholder).
app.initializers.add('voucha-theme-feed-thumbs', function () {
  extend(DiscussionListItem.prototype, 'mainView', function (vnode) {
    var d = this.attrs.discussion;
    if (!d || !d.attribute || !vnode || !vnode.attrs) return;
    var src = d.attribute('vouchaThumb');
    var video = !src && d.attribute('vouchaVideo');
    if (!src && !video) return;
    vnode.attrs.className = (vnode.attrs.className || '') + ' has-vouchaThumb';
    var gone = function (e) {
      var a = e.target.closest('.DiscussionListItem-main');
      if (a) a.classList.remove('has-vouchaThumb');
      (e.target.closest('.VouchaThumb') || e.target).remove();
    };
    var thumb = video ? m('span', {
      className: 'VouchaThumb VouchaThumb--video',
      'aria-hidden': 'true'
    }, [m('img', {
      src: 'https://i.ytimg.com/vi/' + encodeURIComponent(video) + '/mqdefault.jpg',
      alt: '',
      loading: 'lazy',
      decoding: 'async',
      onerror: gone
    }), m('span', {
      className: 'VouchaThumb-play'
    }, m('i', {
      className: 'fas fa-play'
    }))]) : m('img', {
      className: 'VouchaThumb',
      src: src,
      alt: '',
      loading: 'lazy',
      decoding: 'async',
      onerror: gone
    });
    vnode.children = (vnode.children || []).concat([thumb]);
  });
});

// ---------- Home feed: cards lead with the topic, not the person (as Reddit's home feed) ----------
// On All Discussions and Following, a card's avatar spot shows its main topic (the first tag, in Flarum's
// own order): the flag for countries, the tag's icon on its colour otherwise; tapping it opens the topic.
// The author's name stays on the card. On a tag page the topic is already in the banner, so cards keep
// the author's avatar there.
function vouchaSortTags(tags) {
  return tags.slice(0).sort(function (a, b) {
    var ap = a.position(),
      bp = b.position();
    if (ap === null && bp === null) return (b.discussionCount() || 0) - (a.discussionCount() || 0);
    if (bp === null) return -1;
    if (ap === null) return 1;
    var aParent = a.parent(),
      bParent = b.parent();
    if (aParent === bParent) return ap - bp;
    if (aParent && bParent) return aParent.position() - bParent.position();
    if (aParent) return aParent === b ? 1 : aParent.position() - bp;
    if (bParent) return bParent === a ? -1 : ap - bParent.position();
    return 0;
  });
}
function vouchaMainTag(d) {
  var tags = d && d.tags && d.tags() || [];
  tags = tags.filter(function (t) {
    return t && t.position;
  });
  return tags.length ? vouchaSortTags(tags)[0] : null;
}
function vouchaTopicLed() {
  if (currentTagSlug()) return false;
  try {
    return !!(app.current && app.current.matches && app.current.matches(IndexPage));
  } catch (e) {
    return false;
  }
}
app.initializers.add('voucha-theme-topic-led-cards', function () {
  extend(DiscussionListItem.prototype, 'authorItems', function (items) {
    if (!vouchaTopicLed()) return;
    var tag = vouchaMainTag(this.attrs.discussion);
    if (!tag) return;
    var icon = String(tag.icon() || '');
    var flag = /\bvflag\b/.test(icon);
    var href = app.route('tag', {
      tags: tag.slug()
    });
    // pale tag colours (Sports' yellow) get a dark icon, so it stays visible
    var hex = /^#?([0-9a-f]{6})$/i.exec(String(tag.color() || '').trim());
    var light = false;
    if (hex) {
      var n = parseInt(hex[1], 16);
      light = (0.299 * (n >> 16 & 255) + 0.587 * (n >> 8 & 255) + 0.114 * (n & 255)) / 255 > 0.7;
    }
    items.add('vouchaTopic', m('a', {
      className: 'VouchaTopicAvatar' + (flag ? ' ' + icon : light ? ' is-light' : ''),
      href: href,
      title: tag.name(),
      'aria-label': tag.name(),
      style: flag ? undefined : {
        '--topic-color': tag.color() || '#5b6770'
      },
      onclick: function (e) {
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        e.stopPropagation();
        m.route.set(href);
      }
    }, flag ? null : m('i', {
      className: (icon || 'fas fa-hashtag') + ' VouchaTopicAvatar-icon',
      'aria-hidden': 'true'
    })), 110);
  });
  extend(DiscussionListItem.prototype, 'elementAttrs', function (attrs) {
    if (vouchaTopicLed() && vouchaMainTag(this.attrs.discussion)) attrs.className = (attrs.className || '') + ' is-topicLed';
  });
});